import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import AccountSearchSelect from '../components/AccountSearchSelect';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ExclamationTriangleIcon, ArrowPathIcon, ChartBarIcon, GlobeAltIcon, TrashIcon, CodeBracketIcon, DocumentTextIcon, ExclamationCircleIcon, ArrowRightOnRectangleIcon, ArrowsRightLeftIcon, XMarkIcon, EyeSlashIcon, PencilSquareIcon, CheckIcon, LinkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import UtmAttributionCard from '../components/UtmAttributionCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const buildV2RevampExtraction = (requirements = {}) => {
  const extractedInfo = requirements.extractedInfo || {};
  const selectedDesign =
    requirements.selectedDribbbleDesigns?.[0] ||
    extractedInfo.selectedDribbbleDesigns?.[0] ||
    requirements.brandInfo?.selectedDesign ||
    null;
  const bandManifest = Array.isArray(requirements.bandManifest)
    ? requirements.bandManifest
    : Array.isArray(extractedInfo.bandManifest)
      ? extractedInfo.bandManifest
      : [];
  const extractedAssets = Array.isArray(requirements.extractedAssets)
    ? requirements.extractedAssets
    : Array.isArray(extractedInfo.extractedAssets)
      ? extractedInfo.extractedAssets
      : [];
  const extractedStyles = extractedInfo.extractedStyles || requirements.extractedStyles || null;
  const layoutSpec =
    selectedDesign?.layoutSpec ||
    extractedInfo.designLayoutSpec ||
    requirements.designLayoutSpec ||
    null;
  const sourceMockupUrl =
    selectedDesign?.imageUrl ||
    selectedDesign?.mockupImageR2Url ||
    selectedDesign?.layoutSpec?.mockupImage ||
    requirements.brandInfo?.selectedDesign?.imageUrl ||
    null;
  const designMetadata = extractedInfo.designMetadata || selectedDesign || null;
  const faithfulExtractionUsed =
    extractedInfo.faithfulExtractionUsed ??
    requirements.faithfulExtractionUsed ??
    (bandManifest.length > 0 ? true : null);

  const hasAnyData = Boolean(
    sourceMockupUrl ||
    selectedDesign ||
    designMetadata ||
    extractedStyles ||
    layoutSpec ||
    bandManifest.length > 0 ||
    extractedAssets.length > 0 ||
    extractedInfo.faithfulSanity ||
    extractedInfo.extractionError
  );

  if (!hasAnyData) return null;

  return {
    selectedDesign,
    designMetadata,
    sourceMockupUrl,
    layoutSpec,
    extractedStyles,
    bandManifest,
    extractedAssets,
    faithfulExtractionUsed,
    faithfulSanity: extractedInfo.faithfulSanity || null,
    extractedStylesCompletedAt: extractedInfo.extractedStylesCompletedAt || null,
    extractionPending: requirements.extractionPending ?? null,
    extractionError: extractedInfo.extractionError || null,
    extractionRetryCount: extractedInfo.extractionRetryCount ?? null
  };
};

const WebsiteDetail = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [utmAttribution, setUtmAttribution] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // 'mark-failed' | 'retry' | 'regenerate' | 'republish-domain' | 'delete' | 'refresh-ecommerce' | 'unpublish' | null
  const [switchingToVersion, setSwitchingToVersion] = useState(null); // version number being switched to
  const [purgingVersions, setPurgingVersions] = useState(false);
  
  // Page SEO Meta state
  const [pagesMeta, setPagesMeta] = useState([]);
  const [pagesMetaLoading, setPagesMetaLoading] = useState(false);
  const [editingPageMeta, setEditingPageMeta] = useState(null); // pageIndex being edited
  const [metaFormData, setMetaFormData] = useState({ title: '', description: '', keywords: '' });
  const [languageFormData, setLanguageFormData] = useState({}); // { lang: { title, description } }
  const [savingMeta, setSavingMeta] = useState(false);
  const [siteLanguages, setSiteLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  const [isMultiLanguage, setIsMultiLanguage] = useState(false);
  
  // Migrate to Account modal state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateTargetAccount, setMigrateTargetAccount] = useState(null);
  const [migrateNewName, setMigrateNewName] = useState('');
  const [migrateDirection, setMigrateDirection] = useState('qa_to_production');
  const [migrateOperation, setMigrateOperation] = useState('duplicate');
  const [migrateIncludeAllVersions, setMigrateIncludeAllVersions] = useState(false);
  const [migrationJob, setMigrationJob] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationAvailable, setMigrationAvailable] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState('qa');
  const migrationPollTimeoutRef = useRef(null);
  const migrationPollingActiveRef = useRef(false);
  const isMountedRef = useRef(true);

  // Transfer Ownership modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetAccount, setTransferTargetAccount] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);

  // QA CRM / Omni share-link mint result (shown after a successful mint)
  const [crmShareLink, setCrmShareLink] = useState(null); // { url, expiresAt, path, ttlDays }

  // Page limit state
  const [editingPageLimit, setEditingPageLimit] = useState(false);
  const [pageLimitValue, setPageLimitValue] = useState('');
  const [savingPageLimit, setSavingPageLimit] = useState(false);

  // Product limit state (e-commerce sites only)
  const [editingProductLimit, setEditingProductLimit] = useState(false);
  const [productLimitValue, setProductLimitValue] = useState('');
  const [savingProductLimit, setSavingProductLimit] = useState(false);

  // AI customization quota state
  const [aiQuotaInfo, setAiQuotaInfo] = useState(null); // { quota, topups, isTrialTier, accountTier }
  const [aiQuotaLoading, setAiQuotaLoading] = useState(false);
  const [aiQuotaLoadFailed, setAiQuotaLoadFailed] = useState(false);
  const [editingAiQuotaLimit, setEditingAiQuotaLimit] = useState(false);
  const [aiQuotaLimitValue, setAiQuotaLimitValue] = useState('');
  const [editingMonthlyQuotaLimit, setEditingMonthlyQuotaLimit] = useState(false);
  const [monthlyQuotaLimitValue, setMonthlyQuotaLimitValue] = useState('');
  const [aiTopupCredits, setAiTopupCredits] = useState('100');
  const [savingAiQuota, setSavingAiQuota] = useState(false);
  const [grantingAiTopup, setGrantingAiTopup] = useState(false);
  const [whatsappQuotaInfo, setWhatsappQuotaInfo] = useState(null);
  const [whatsappQuotaLoading, setWhatsappQuotaLoading] = useState(false);
  const [whatsappQuotaLoadFailed, setWhatsappQuotaLoadFailed] = useState(false);
  const [whatsappTopupCredits, setWhatsappTopupCredits] = useState('100');
  const [grantingWhatsappTopup, setGrantingWhatsappTopup] = useState(false);
  const [voiceMinutesInfo, setVoiceMinutesInfo] = useState(null);
  const [voiceMinutesLoading, setVoiceMinutesLoading] = useState(false);
  const [voiceMinutesLoadFailed, setVoiceMinutesLoadFailed] = useState(false);
  const [voiceGrantMinutes, setVoiceGrantMinutes] = useState('100');
  const [grantingVoiceMinutes, setGrantingVoiceMinutes] = useState(false);
  const [voiceMonthlyMinutes, setVoiceMonthlyMinutes] = useState('0');
  const [savingVoiceMonthly, setSavingVoiceMonthly] = useState(false);
  // Guards against out-of-order responses when the admin switches websites
  // quickly: only the latest WhatsApp-quota request may write state.
  const whatsappQuotaRequestIdRef = useRef(0);
  const voiceMinutesRequestIdRef = useRef(0);
  const currentWebsiteIdRef = useRef(websiteId);
  currentWebsiteIdRef.current = websiteId;

  // WhatsApp Lead Bot monthly conversation cap (distinct customers/month).
  // Null when the site has no bot provisioned (section is hidden then).
  const [waBotLimit, setWaBotLimit] = useState(null); // { override, used, limit, remaining, reached, periodYm }
  const [editingWaBotLimit, setEditingWaBotLimit] = useState(false);
  const [waBotLimitValue, setWaBotLimitValue] = useState('');
  const [savingWaBotLimit, setSavingWaBotLimit] = useState(false);

  // Rename state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // E-commerce stats state
  const [ecommerceStats, setEcommerceStats] = useState(null);
  const [ecommerceStatsLoading, setEcommerceStatsLoading] = useState(false);

  useEffect(() => {
    fetchWebsiteDetails();
    setGrantingWhatsappTopup(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      migrationPollingActiveRef.current = false;
      if (migrationPollTimeoutRef.current) {
        clearTimeout(migrationPollTimeoutRef.current);
        migrationPollTimeoutRef.current = null;
      }
    };
  }, []);
  
  useEffect(() => {
    if (website?.content) {
      fetchPagesMeta();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website?.id]);

  useEffect(() => {
    if (website?.site_type === 'ecommerce') {
      fetchEcommerceStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website?.id, website?.site_type]);

  useEffect(() => {
    if (website?.id) {
      setAiQuotaInfo(null);
      setAiQuotaLoadFailed(false);
      setAiQuotaLoading(true);
      fetchWaBotLimit();
      fetchAiQuota();
      fetchWhatsappQuota();
      fetchVoiceMinutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website?.id]);

  // Check if cross-environment migration is available
  useEffect(() => {
    const checkMigrationStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/websites/migration-status`);
        console.log('Migration status response:', response.data);
        const data = response.data.data;
        console.log('Migration available:', data.available, 'Current env:', data.currentEnvironment);
        setMigrationAvailable(data.available);
        setCurrentEnvironment(data.currentEnvironment || 'qa');
      } catch (error) {
        console.error('Error checking migration status:', error);
        setMigrationAvailable(false);
      }
    };
    checkMigrationStatus();
  }, []);

  // Clear selected account when migration direction changes
  useEffect(() => {
    setMigrateTargetAccount(null);
  }, [migrateDirection]);

  const fetchWebsiteDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}`);
      setWebsite(response.data.data.website);
      setUtmAttribution(response.data.data.utmAttribution || null);
      setVersions(response.data.data.versions || []);
    } catch (error) {
      console.error('Error fetching website:', error);
      toast.error('Failed to fetch website details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPagesMeta = async () => {
    try {
      setPagesMetaLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/pages-meta`);
      const data = response.data.data;
      setPagesMeta(data.pages || []);
      setSiteLanguages(data.languages || []);
      setDefaultLanguage(data.defaultLanguage || null);
      setIsMultiLanguage(data.isMultiLanguage || false);
    } catch (error) {
      console.error('Error fetching pages meta:', error);
      // Don't show error toast - this is a supplementary feature
    } finally {
      setPagesMetaLoading(false);
    }
  };

  const fetchEcommerceStats = async () => {
    try {
      setEcommerceStatsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/ecommerce-stats`);
      setEcommerceStats(response.data.data);
    } catch (error) {
      console.error('Error fetching e-commerce stats:', error);
    } finally {
      setEcommerceStatsLoading(false);
    }
  };

  const handleEditPageMeta = (page) => {
    setEditingPageMeta(page.pageIndex);
    setMetaFormData({
      title: page.meta?.title || '',
      description: page.meta?.description || '',
      keywords: Array.isArray(page.meta?.keywords) ? page.meta.keywords.join(', ') : ''
    });
    
    // Initialize per-language form data
    if (page.isMultiLanguage && page.languageData) {
      const langData = {};
      Object.entries(page.languageData).forEach(([lang, data]) => {
        langData[lang] = {
          title: data.title || '',
          description: data.description || ''
        };
      });
      setLanguageFormData(langData);
    } else {
      setLanguageFormData({});
    }
  };

  const handleCancelEditMeta = () => {
    setEditingPageMeta(null);
    setMetaFormData({ title: '', description: '', keywords: '' });
    setLanguageFormData({});
  };

  const handleLanguageFormChange = (lang, field, value) => {
    setLanguageFormData(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value
      }
    }));
  };

  const handleSavePageMeta = async (pageIndex, syncToHtml = false) => {
    try {
      setSavingMeta(true);
      
      const keywords = metaFormData.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const pageUpdate = {
        pageIndex,
        syncToHtml
      };

      // For multi-language sites, send languageUpdates
      if (isMultiLanguage && Object.keys(languageFormData).length > 0) {
        pageUpdate.languageUpdates = languageFormData;
      }
      
      // Also send meta for keywords and single-language fallback
      pageUpdate.meta = {
        keywords
      };
      
      // For single-language sites, include title/description in meta
      if (!isMultiLanguage) {
        pageUpdate.meta.title = metaFormData.title;
        pageUpdate.meta.description = metaFormData.description;
      }

      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}/pages-meta`, {
        pages: [pageUpdate]
      });

      toast.success('Page meta updated successfully');
      setEditingPageMeta(null);
      setMetaFormData({ title: '', description: '', keywords: '' });
      setLanguageFormData({});
      
      // Refresh data
      fetchPagesMeta();
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error saving page meta:', error);
      toast.error(error.response?.data?.error || 'Failed to save page meta');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleSwitchToVersion = async (targetVersion) => {
    if (!window.confirm(`Are you sure you want to switch to Version ${targetVersion}? This will restore the website content from that version.`)) {
      return;
    }
    
    try {
      setSwitchingToVersion(targetVersion);
      await axios.post(`${API_BASE_URL}/api/website/${websiteId}/switch-version`, {
        targetVersion: targetVersion
      });
      toast.success(`Successfully switched to Version ${targetVersion}`);
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error switching version:', error);
      toast.error(error.response?.data?.error || 'Failed to switch version');
    } finally {
      setSwitchingToVersion(null);
    }
  };

  const handlePurgeVersions = async () => {
    // Calculate which versions will be kept: 1, 2, and current
    const currentVersion = website.current_version || 1;
    const versionsToKeep = new Set([1, 2, currentVersion]);
    const versionsToPurge = versions.filter(v => !versionsToKeep.has(v.version));
    
    if (versionsToPurge.length === 0) {
      toast.error('No versions to purge (only base versions 1, 2 and current exist)');
      return;
    }
    
    const keptVersionsList = [...versionsToKeep].filter(v => versions.some(ver => ver.version === v)).sort((a, b) => a - b);
    
    if (!window.confirm(
      `Are you sure you want to delete ${versionsToPurge.length} version(s)?\n\n` +
      `This will keep versions ${keptVersionsList.join(', ')} (base site + current) ` +
      `and remove all intermediate versions.\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }
    
    try {
      setPurgingVersions(true);
      const response = await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/purge-versions`);
      const { deletedCount, keptVersions } = response.data.data;
      toast.success(`Purged ${deletedCount} version(s), kept versions ${keptVersions.join(', ')}`);
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error purging versions:', error);
      toast.error(error.response?.data?.error || 'Failed to purge versions');
    } finally {
      setPurgingVersions(false);
    }
  };

  const handleToggleMaintenance = async () => {
    const isEnabling = !website.maintenance_mode;
    const confirmMsg = isEnabling
      ? 'Enable maintenance mode? The website owner will not be able to access or edit their website while maintenance mode is active.'
      : 'Disable maintenance mode? The website owner will regain full access to their website.';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      setActionLoading('toggle-maintenance');
      await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/toggle-maintenance`);
      toast.success(`Maintenance mode ${isEnabling ? 'enabled' : 'disabled'}`);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      toast.error(error.response?.data?.error || 'Failed to toggle maintenance mode');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAsFailed = async () => {
    if (!window.confirm('Are you sure you want to mark this website as failed? This will stop the current generation process.')) {
      return;
    }
    
    try {
      setActionLoading('mark-failed');
      await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/mark-failed`);
      toast.success('Website marked as failed');
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error marking website as failed:', error);
      toast.error(error.response?.data?.error || 'Failed to mark website as failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryGeneration = async () => {
    if (!window.confirm('Are you sure you want to retry website generation? This will restart the generation process.')) {
      return;
    }
    
    try {
      setActionLoading('retry');
      await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/retry`);
      toast.success('Website regeneration started');
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error retrying website generation:', error);
      toast.error(error.response?.data?.error || 'Failed to retry website generation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerateWebsite = async () => {
    if (!window.confirm('Are you sure you want to regenerate this website? This will create a new version using the saved requirements. The old deployment will be deleted.')) {
      return;
    }
    
    try {
      setActionLoading('regenerate');
      const response = await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/regenerate`);
      toast.success('Website regeneration started with saved requirements!');
      
      // Show details about what's being regenerated
      const reqInfo = response.data.data?.requirements;
      if (reqInfo) {
        toast.success(`Regenerating ${reqInfo.businessName || 'website'} (${reqInfo.industry || 'business'})`, {
          duration: 4000
        });
      }
      
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error regenerating website:', error);
      toast.error(error.response?.data?.error || 'Failed to regenerate website');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRepublishDomain = async () => {
    if (!window.confirm('Are you sure you want to republish the domain? This will reconfigure the domain on Render and Cloudflare, and warm the cache. This may take a few minutes.')) {
      return;
    }
    
    try {
      setActionLoading('republish-domain');
      toast.loading('Republishing domain... This may take a few minutes.', { id: 'republish-domain' });
      
      const response = await axios.post(`${API_BASE_URL}/api/admin/websites/${websiteId}/republish-domain`);
      
      toast.dismiss('republish-domain');
      
      if (response.data.success) {
        const { steps } = response.data.data;
        const successCount = steps.filter(s => s.status === 'success').length;
        const warningCount = steps.filter(s => s.status === 'warning').length;
        
        if (warningCount > 0) {
          toast.success(`Domain republished with ${warningCount} warning(s). ${successCount} steps completed.`);
        } else {
          toast.success('Domain republished successfully!');
        }
      }
      
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      toast.dismiss('republish-domain');
      console.error('Error republishing domain:', error);
      toast.error(error.response?.data?.error || 'Failed to republish domain');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteWebsite = async () => {
    if (!window.confirm(`Are you sure you want to delete "${website.name}"? This action cannot be undone and will permanently remove the website and all associated data.`)) {
      return;
    }
    
    // Double confirmation for safety
    if (!window.confirm('This is your final warning. The website, all versions, analytics data, and deployment will be permanently deleted. Continue?')) {
      return;
    }
    
    try {
      setActionLoading('delete');
      await axios.delete(`${API_BASE_URL}/api/admin/websites/${websiteId}`);
      toast.success('Website deleted successfully');
      navigate('/websites');
    } catch (error) {
      console.error('Error deleting website:', error);
      toast.error(error.response?.data?.error || 'Failed to delete website');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefreshEcommerceScripts = async () => {
    if (!window.confirm('Are you sure you want to refresh e-commerce scripts? This will re-apply the latest e-commerce CSS and JavaScript without changing the site content.')) {
      return;
    }
    
    try {
      setActionLoading('refresh-ecommerce');
      await axios.post(`${API_BASE_URL}/api/ecommerce/refresh-scripts`, {
        websiteId
      });
      toast.success('E-commerce scripts refreshed successfully');
      fetchWebsiteDetails(); // Refresh the data
    } catch (error) {
      console.error('Error refreshing e-commerce scripts:', error);
      toast.error(error.response?.data?.error || 'Failed to refresh e-commerce scripts');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDowngradeToMarketing = async () => {
    const removePages = window.confirm(
      'Downgrade this site to a Marketing site?\n\nClick OK to also remove e-commerce pages (products, cart, checkout, etc.).\nClick Cancel to keep the pages but change the site type only.'
    );

    if (!window.confirm(`Final confirmation: downgrade "${website.name}" to Marketing?`)) {
      return;
    }

    try {
      setActionLoading('downgrade-marketing');
      await axios.post(`${API_BASE_URL}/api/ecommerce/${websiteId}/downgrade`, {
        removeEcommercePages: removePages
      });
      toast.success('Site downgraded to Marketing successfully');
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error downgrading to marketing:', error);
      toast.error(error.response?.data?.error || 'Failed to downgrade site');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublishWebsite = async () => {
    if (!window.confirm(`Are you sure you want to unpublish "${website.name}"? This will take the live site offline and remove the deployment.`)) {
      return;
    }

    try {
      setActionLoading('unpublish');
      await axios.post(`${API_BASE_URL}/api/website/${websiteId}/unpublish`);
      toast.success('Website unpublished successfully');
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error unpublishing website:', error);
      toast.error(error.response?.data?.error || 'Failed to unpublish website');
    } finally {
      setActionLoading(null);
    }
  };

  // Open migrate modal
  const handleOpenMigrateModal = () => {
    setMigrateNewName(currentEnvironment === 'production' ? `${website.name} (QA Copy)` : website.name);
    setMigrateTargetAccount(null);
    setMigrateDirection(currentEnvironment === 'production' ? 'production_to_qa' : 'qa_to_production');
    setMigrateOperation('duplicate');
    setMigrateIncludeAllVersions(false);
    setMigrationJob(null);
    setShowMigrateModal(true);
  };

  // Close migrate modal
  const handleCloseMigrateModal = () => {
    if (isMigrating) {
      toast.error('Migration is still running. Please wait for it to finish before closing this dialog.');
      return;
    }

    migrationPollingActiveRef.current = false;
    if (migrationPollTimeoutRef.current) {
      clearTimeout(migrationPollTimeoutRef.current);
      migrationPollTimeoutRef.current = null;
    }

    setShowMigrateModal(false);
    setMigrateTargetAccount(null);
    setMigrateNewName('');
    setMigrateDirection('qa_to_production');
    setMigrateOperation('duplicate');
    setMigrateIncludeAllVersions(false);
    setMigrationJob(null);
  };

  const pollMigrationJob = async (jobId, context, retryCount = 0) => {
    if (!migrationPollingActiveRef.current || !isMountedRef.current) {
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/migrate-to-account/jobs/${jobId}`
      );
      const job = response.data.data;
      if (!migrationPollingActiveRef.current || !isMountedRef.current) {
        return;
      }

      setMigrationJob(job);

      if (job.status === 'completed') {
        migrationPollingActiveRef.current = false;
        const result = job.result || {};
        const copiedVersions = result.versionsCopied || (job.includeAllVersions ? 'all' : 1);
        toast.success(
          `Website ${context.isDuplicate ? 'copy' : 'move'} completed (${context.directionLabel}); copied ${copiedVersions} version${copiedVersions === 1 ? '' : 's'}.`
        );
        setIsMigrating(false);

        if (context.targetEnv.toLowerCase() === currentEnvironment && result.newWebsiteId) {
          navigate(`/websites/${result.newWebsiteId}`);
        } else {
          toast.success(
            context.isDuplicate
              ? `Website copy was created in the ${context.targetEnv} database; the original remains in ${context.sourceEnv}.`
              : `Website was moved to the ${context.targetEnv} database.`
          );
          navigate('/websites');
        }
        return;
      }

      if (job.status === 'failed') {
        migrationPollingActiveRef.current = false;
        toast.error(job.errorMessage || 'Environment migration failed');
        setIsMigrating(false);
        return;
      }

      migrationPollTimeoutRef.current = setTimeout(() => pollMigrationJob(jobId, context, 0), 3000);
    } catch (error) {
      console.error('Error polling migration job:', error);
      if (!migrationPollingActiveRef.current || !isMountedRef.current) {
        return;
      }

      if (retryCount === 0) {
        toast.error('Temporary issue checking migration status. Keeping the job locked and retrying...');
      }
      const nextDelay = Math.min(15000, 3000 + retryCount * 2000);
      migrationPollTimeoutRef.current = setTimeout(
        () => pollMigrationJob(jobId, context, retryCount + 1),
        nextDelay
      );
    }
  };

  // Perform migration
  const handleMigrateToAccount = async () => {
    if (!migrateTargetAccount) {
      toast.error('Please select a target account');
      return;
    }

    const directionLabel = migrateDirection === 'qa_to_production' 
      ? 'QA → Production' 
      : 'Production → QA';
    const sourceEnv = migrateDirection === 'qa_to_production' ? 'QA' : 'Production';
    const targetEnv = migrateDirection === 'qa_to_production' ? 'Production' : 'QA';

    const isDuplicate = migrateOperation === 'duplicate';
    const operationLabel = isDuplicate ? 'duplicate' : 'migrate';

    // Confirm the operation
    if (!window.confirm(
      `Are you sure you want to ${operationLabel} "${website.name}" (${directionLabel}) to account "${migrateTargetAccount.name}"?\n\n` +
      `This will:\n` +
      `• Create a new website record in the ${targetEnv} database\n` +
      `• Copy all content, e-commerce data, ${migrateIncludeAllVersions ? 'all website versions' : 'only the current website version'}, and supported site data\n` +
      `• Run in the background; you can follow progress in this dialog\n` +
      (isDuplicate
        ? `• Keep the original website in the ${sourceEnv} database\n` +
          `• Remove live deployment/domain handles from the copy for QA safety\n\n`
        : `• DELETE this website from the ${sourceEnv} database\n\n`) +
      (isDuplicate ? `The original site will remain unchanged.` : `This action cannot be undone.`)
    )) {
      return;
    }

    try {
      setIsMigrating(true);
      migrationPollingActiveRef.current = true;
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/migrate-to-account`,
        {
          targetAccountId: migrateTargetAccount.id,
          direction: migrateDirection,
          operation: migrateOperation,
          includeAllVersions: migrateIncludeAllVersions,
          newName: migrateNewName || undefined
        }
      );

      const jobId = response.data.data.jobId;
      setMigrationJob({
        id: jobId,
        status: response.data.data.status,
        progress: { phase: 'queued' },
        includeAllVersions: response.data.data.includeAllVersions
      });
      toast.success(`Website ${isDuplicate ? 'duplicate' : 'migration'} queued (${directionLabel})`);
      pollMigrationJob(jobId, { directionLabel, sourceEnv, targetEnv, isDuplicate });
    } catch (error) {
      console.error('Error migrating website:', error);
      migrationPollingActiveRef.current = false;
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to migrate website');
      setIsMigrating(false);
    }
  };

  // Open transfer ownership modal
  const handleOpenTransferModal = () => {
    setTransferTargetAccount(null);
    setShowTransferModal(true);
  };

  // Close transfer ownership modal
  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setTransferTargetAccount(null);
  };

  /**
   * Mint a QA CRM / Omni share link (no real login) and copy it to the clipboard.
   * Requires FEATURE_CRM_SHARE_LINK + secret + host allowlist on this environment.
   */
  const handleMintCrmShareLink = async (path = 'crm') => {
    const actionKey = path === 'omni' ? 'crm-share-omni' : 'crm-share-crm';
    try {
      setActionLoading(actionKey);
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/crm-share-link`,
        { path, ttlDays: 7 }
      );
      const data = response.data?.data;
      if (!data?.url) {
        throw new Error(response.data?.error || 'No share URL returned');
      }
      setCrmShareLink(data);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success(
          `${path === 'omni' ? 'Omni' : 'CRM'} share link copied — expires ${data.expiresAt ? new Date(data.expiresAt).toLocaleString() : 'soon'}`
        );
      } catch (_) {
        toast.success(`${path === 'omni' ? 'Omni' : 'CRM'} share link ready — copy it from the panel below`);
      }
    } catch (error) {
      const code = error.response?.data?.code;
      const message = error.response?.data?.error
        || error.response?.data?.hint
        || error.message
        || 'Failed to mint share link';
      if (code === 'CRM_SHARE_DISABLED' || error.response?.status === 404) {
        toast.error('CRM share links are not enabled on this environment. Set FEATURE_CRM_SHARE_LINK + CRM_SHARE_SECRET + CRM_SHARE_ALLOWED_HOSTS.');
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyCrmShareLink = async () => {
    if (!crmShareLink?.url) return;
    try {
      await navigator.clipboard.writeText(crmShareLink.url);
      toast.success('Share link copied');
    } catch (_) {
      toast.error('Could not copy — select the URL manually');
    }
  };

  // Perform ownership transfer
  const handleTransferOwnership = async () => {
    if (!transferTargetAccount) {
      toast.error('Please select a target account');
      return;
    }

    if (!window.confirm(
      `Are you sure you want to transfer "${website.name}" to account "${transferTargetAccount.name}"?\n\n` +
      `This will change the website owner to ${transferTargetAccount.primaryContactName || transferTargetAccount.name} (${transferTargetAccount.primaryEmail || 'no email'}).\n\n` +
      `The website content, deployment, and all settings will be preserved.`
    )) {
      return;
    }

    try {
      setIsTransferring(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/transfer-ownership`,
        {
          targetAccountId: transferTargetAccount.id
        }
      );

      toast.success(`Website transferred to "${response.data.data.newOwner.accountName}"`);
      handleCloseTransferModal();
      // Refresh website details to show updated owner
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error transferring website:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to transfer website');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleEditPageLimit = () => {
    setEditingPageLimit(true);
    setPageLimitValue(website.page_limit != null ? String(website.page_limit) : '');
  };

  const handleSavePageLimit = async () => {
    try {
      setSavingPageLimit(true);
      const value = pageLimitValue.trim();
      const parsedValue = value === '' ? null : parseInt(value, 10);

      if (parsedValue !== null && (isNaN(parsedValue) || parsedValue < 1)) {
        toast.error('Page limit must be a positive number or empty for default');
        return;
      }

      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        page_limit: parsedValue
      });

      toast.success(parsedValue ? `Page limit set to ${parsedValue}` : 'Page limit reset to global default');
      setEditingPageLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error updating page limit:', error);
      toast.error(error.response?.data?.error || 'Failed to update page limit');
    } finally {
      setSavingPageLimit(false);
    }
  };

  const handleResetPageLimit = async () => {
    try {
      setSavingPageLimit(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        page_limit: null
      });
      toast.success('Page limit reset to global default');
      setEditingPageLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error resetting page limit:', error);
      toast.error('Failed to reset page limit');
    } finally {
      setSavingPageLimit(false);
    }
  };

  const handleEditProductLimit = () => {
    setEditingProductLimit(true);
    setProductLimitValue(website.product_limit != null ? String(website.product_limit) : '');
  };

  const handleSaveProductLimit = async () => {
    try {
      setSavingProductLimit(true);
      const value = productLimitValue.trim();
      const parsedValue = value === '' ? null : parseInt(value, 10);

      if (parsedValue !== null && (Number.isNaN(parsedValue) || parsedValue < 1)) {
        toast.error('Product limit must be a positive number or empty for default');
        return;
      }

      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        product_limit: parsedValue
      });

      toast.success(parsedValue ? `Product limit set to ${parsedValue}` : 'Product limit reset to global default');
      setEditingProductLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error updating product limit:', error);
      toast.error(error.response?.data?.error || 'Failed to update product limit');
    } finally {
      setSavingProductLimit(false);
    }
  };

  const handleResetProductLimit = async () => {
    try {
      setSavingProductLimit(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        product_limit: null
      });
      toast.success('Product limit reset to global default');
      setEditingProductLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error resetting product limit:', error);
      toast.error('Failed to reset product limit');
    } finally {
      setSavingProductLimit(false);
    }
  };

  const handleEditAiQuotaLimit = () => {
    setEditingAiQuotaLimit(true);
    setAiQuotaLimitValue(
      website.ai_customization_limit != null ? String(website.ai_customization_limit) : ''
    );
  };

  const handleSaveAiQuotaLimit = async () => {
    try {
      setSavingAiQuota(true);
      const value = aiQuotaLimitValue.trim();
      const parsedValue = value === '' ? null : parseInt(value, 10);

      if (parsedValue !== null && (isNaN(parsedValue) || parsedValue < 1)) {
        toast.error('AI customization limit must be a positive number or empty for default');
        return;
      }

      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_customization_limit: parsedValue,
      });
      toast.success(
        parsedValue
          ? `AI customization limit set to ${parsedValue}`
          : 'AI customization limit reset to trial default'
      );
      setEditingAiQuotaLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error updating AI customization limit:', error);
      toast.error(error.response?.data?.error || 'Failed to update AI customization limit');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const handleResetAiQuotaLimit = async () => {
    try {
      setSavingAiQuota(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_customization_limit: null,
      });
      toast.success('AI customization limit reset to trial default');
      setEditingAiQuotaLimit(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error resetting AI customization limit:', error);
      toast.error('Failed to reset AI customization limit');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const handleResetAiQuotaUsage = async () => {
    if (!window.confirm('Reset this site\'s AI customization usage counter back to 0?')) {
      return;
    }
    try {
      setSavingAiQuota(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_customization_count: 0,
      });
      toast.success('AI customization usage reset to 0');
      fetchWebsiteDetails();
      fetchAiQuota();
    } catch (error) {
      console.error('Error resetting AI customization usage:', error);
      toast.error(error.response?.data?.error || 'Failed to reset AI customization usage');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const fetchAiQuota = async () => {
    try {
      setAiQuotaInfo(null);
      setAiQuotaLoading(true);
      setAiQuotaLoadFailed(false);
      const resp = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/ai-quota`);
      setAiQuotaInfo(resp.data?.data || null);
    } catch (error) {
      console.error('Error fetching AI quota:', error);
      setAiQuotaInfo(null);
      setAiQuotaLoadFailed(true);
    } finally {
      setAiQuotaLoading(false);
    }
  };

  const handleResetMonthlyAiUsage = async () => {
    if (!window.confirm('Reset this site\'s monthly AI usage counter back to 0?')) {
      return;
    }
    try {
      setSavingAiQuota(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_monthly_usage_count: 0,
      });
      toast.success('Monthly AI usage reset to 0');
      fetchWebsiteDetails();
      fetchAiQuota();
    } catch (error) {
      console.error('Error resetting monthly AI usage:', error);
      toast.error(error.response?.data?.error || 'Failed to reset monthly AI usage');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const handleEditMonthlyQuotaLimit = () => {
    setEditingMonthlyQuotaLimit(true);
    setMonthlyQuotaLimitValue(
      website?.ai_monthly_quota_limit != null ? String(website.ai_monthly_quota_limit) : ''
    );
  };

  const handleSaveMonthlyQuotaLimit = async () => {
    try {
      setSavingAiQuota(true);
      const value = monthlyQuotaLimitValue.trim();
      const parsedValue = value === '' ? null : parseInt(value, 10);

      if (parsedValue !== null && (Number.isNaN(parsedValue) || parsedValue < 1)) {
        toast.error('Monthly AI limit must be a positive number or empty for default');
        return;
      }

      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_monthly_quota_limit: parsedValue,
      });
      toast.success(
        parsedValue
          ? `Monthly AI limit set to ${parsedValue}`
          : 'Monthly AI limit reset to tier default'
      );
      setEditingMonthlyQuotaLimit(false);
      fetchWebsiteDetails();
      fetchAiQuota();
    } catch (error) {
      console.error('Error updating monthly AI limit:', error);
      toast.error(error.response?.data?.error || 'Failed to update monthly AI limit');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const handleResetMonthlyQuotaLimit = async () => {
    try {
      setSavingAiQuota(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, {
        ai_monthly_quota_limit: null,
      });
      toast.success('Monthly AI limit reset to tier default');
      setEditingMonthlyQuotaLimit(false);
      fetchWebsiteDetails();
      fetchAiQuota();
    } catch (error) {
      console.error('Error resetting monthly AI limit:', error);
      toast.error('Failed to reset monthly AI limit');
    } finally {
      setSavingAiQuota(false);
    }
  };

  const handleGrantAiTopup = async () => {
    const parsedCredits = parseInt(aiTopupCredits, 10);
    if (Number.isNaN(parsedCredits) || parsedCredits < 1 || parsedCredits > 10000) {
      toast.error('Enter a credit amount between 1 and 10,000');
      return;
    }
    if (!window.confirm(`Grant ${parsedCredits} complimentary AI credits to this site (no payment)?`)) {
      return;
    }
    try {
      setGrantingAiTopup(true);
      const resp = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/ai-quota/topup`,
        { credits: parsedCredits }
      );
      setAiQuotaInfo(resp.data?.data || null);
      toast.success(`Granted ${parsedCredits} AI credits`);
    } catch (error) {
      console.error('Error granting AI top-up:', error);
      toast.error(error.response?.data?.error || 'Failed to grant AI top-up');
    } finally {
      setGrantingAiTopup(false);
    }
  };

  const fetchWhatsappQuota = async () => {
    const requestId = ++whatsappQuotaRequestIdRef.current;
    try {
      setWhatsappQuotaInfo(null);
      setWhatsappQuotaLoading(true);
      setWhatsappQuotaLoadFailed(false);
      const resp = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/whatsapp-quota`);
      if (whatsappQuotaRequestIdRef.current !== requestId) return;
      setWhatsappQuotaInfo(resp.data?.data || null);
    } catch (error) {
      if (whatsappQuotaRequestIdRef.current !== requestId) return;
      console.error('Error fetching WhatsApp quota:', error);
      setWhatsappQuotaInfo(null);
      setWhatsappQuotaLoadFailed(true);
    } finally {
      if (whatsappQuotaRequestIdRef.current === requestId) {
        setWhatsappQuotaLoading(false);
      }
    }
  };

  const fetchVoiceMinutes = async () => {
    const requestId = ++voiceMinutesRequestIdRef.current;
    try {
      setVoiceMinutesInfo(null);
      setVoiceMinutesLoading(true);
      setVoiceMinutesLoadFailed(false);
      const resp = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/voice-minutes`);
      if (voiceMinutesRequestIdRef.current !== requestId) return;
      const data = resp.data?.data || null;
      setVoiceMinutesInfo(data);
      setVoiceMonthlyMinutes(String(data?.quota?.minutesPerMonth ?? 0));
    } catch (error) {
      if (voiceMinutesRequestIdRef.current !== requestId) return;
      console.error('Error fetching call minutes:', error);
      setVoiceMinutesInfo(null);
      setVoiceMinutesLoadFailed(true);
    } finally {
      if (voiceMinutesRequestIdRef.current === requestId) {
        setVoiceMinutesLoading(false);
      }
    }
  };

  const handleGrantVoiceMinutes = async () => {
    const parsed = parseInt(voiceGrantMinutes, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 100000) {
      toast.error('Enter a minute amount between 1 and 100,000');
      return;
    }
    try {
      setGrantingVoiceMinutes(true);
      const resp = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/voice-minutes/topup`,
        { minutes: parsed }
      );
      setVoiceMinutesInfo(resp.data?.data || null);
      toast.success(`Granted ${parsed} call minutes`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to grant call minutes');
    } finally {
      setGrantingVoiceMinutes(false);
    }
  };

  const handleSaveVoiceMonthly = async () => {
    const parsed = parseInt(voiceMonthlyMinutes, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100000) {
      toast.error('Enter 0 to turn the monthly grant off, or up to 100,000 minutes');
      return;
    }
    try {
      setSavingVoiceMonthly(true);
      const resp = await axios.put(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/voice-minutes/monthly`,
        { minutesPerMonth: parsed }
      );
      setVoiceMinutesInfo((current) => ({
        ...(current || {}),
        quota: resp.data?.data?.quota || current?.quota,
      }));
      toast.success(parsed === 0 ? 'Monthly call minutes turned off' : `Will add ${parsed} minutes on the 1st`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save monthly call minutes');
    } finally {
      setSavingVoiceMonthly(false);
    }
  };

  const handleGrantWhatsappTopup = async () => {
    const parsedCredits = parseInt(whatsappTopupCredits, 10);
    if (Number.isNaN(parsedCredits) || parsedCredits < 1 || parsedCredits > 100000) {
      toast.error('Enter a credit amount between 1 and 100,000');
      return;
    }
    if (!window.confirm(`Grant ${parsedCredits} complimentary WhatsApp credits to this site (no payment)?`)) {
      return;
    }
    // Treat the grant as the latest quota request so any older GET response
    // cannot overwrite the fresher quota snapshot returned by the POST.
    const requestIdAtStart = ++whatsappQuotaRequestIdRef.current;
    const websiteIdAtStart = websiteId;
    const isCurrentWebsite = () => String(currentWebsiteIdRef.current) === String(websiteIdAtStart);
    const canReportTopupResult = () => (
      isCurrentWebsite() && whatsappQuotaRequestIdRef.current === requestIdAtStart
    );
    try {
      setGrantingWhatsappTopup(true);
      const resp = await axios.post(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/whatsapp-quota/topup`,
        { credits: parsedCredits }
      );
      // Skip result feedback if the admin switched websites or another
      // WhatsApp-quota request superseded this top-up while it was in flight.
      if (!canReportTopupResult()) return;
      setWhatsappQuotaInfo(resp.data?.data || null);
      toast.success(`Granted ${parsedCredits} WhatsApp credits`);
    } catch (error) {
      if (!canReportTopupResult()) return;
      console.error('Error granting WhatsApp top-up:', error);
      toast.error(error.response?.data?.error || 'Failed to grant WhatsApp top-up');
    } finally {
      if (isCurrentWebsite()) {
        setGrantingWhatsappTopup(false);
      }
    }
  };

  const fetchWaBotLimit = async () => {
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/whatsapp-bot-limit`
      );
      setWaBotLimit(resp.data?.data || null);
    } catch (error) {
      // 404 => no bot provisioned for this site; hide the section.
      setWaBotLimit(null);
    }
  };

  const handleEditWaBotLimit = () => {
    setEditingWaBotLimit(true);
    setWaBotLimitValue(waBotLimit?.override != null ? String(waBotLimit.override) : '');
  };

  const handleSaveWaBotLimit = async () => {
    try {
      setSavingWaBotLimit(true);
      const value = waBotLimitValue.trim();
      const parsedValue = value === '' ? null : parseInt(value, 10);
      if (parsedValue !== null && (isNaN(parsedValue) || parsedValue < 0)) {
        toast.error('Limit must be 0 or a positive number, or empty for the platform default');
        return;
      }
      const resp = await axios.put(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/whatsapp-bot-limit`,
        { monthly_conversation_limit: parsedValue }
      );
      setWaBotLimit(resp.data?.data || null);
      toast.success(
        parsedValue != null
          ? `WhatsApp bot limit set to ${parsedValue}/month`
          : 'WhatsApp bot limit reset to platform default'
      );
      setEditingWaBotLimit(false);
    } catch (error) {
      console.error('Error updating WhatsApp bot limit:', error);
      toast.error(error.response?.data?.error || 'Failed to update WhatsApp bot limit');
    } finally {
      setSavingWaBotLimit(false);
    }
  };

  const handleResetWaBotLimit = async () => {
    try {
      setSavingWaBotLimit(true);
      const resp = await axios.put(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/whatsapp-bot-limit`,
        { monthly_conversation_limit: null }
      );
      setWaBotLimit(resp.data?.data || null);
      toast.success('WhatsApp bot limit reset to platform default');
      setEditingWaBotLimit(false);
    } catch (error) {
      console.error('Error resetting WhatsApp bot limit:', error);
      toast.error('Failed to reset WhatsApp bot limit');
    } finally {
      setSavingWaBotLimit(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) {
      toast.error('Website name cannot be empty');
      return;
    }
    if (trimmed === website.name) {
      setEditingName(false);
      return;
    }
    try {
      setSavingName(true);
      await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}`, { name: trimmed });
      toast.success('Website name updated');
      setEditingName(false);
      fetchWebsiteDetails();
    } catch (error) {
      console.error('Error updating website name:', error);
      toast.error(error.response?.data?.error || 'Failed to update website name');
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!website) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Website not found</p>
        </div>
      </AdminLayout>
    );
  }

  const v2RevampExtraction = buildV2RevampExtraction(website.requirements);

  const aiQuotaReady = aiQuotaInfo != null && !aiQuotaLoading && !aiQuotaLoadFailed;
  const isAiQuotaTrial = aiQuotaReady && aiQuotaInfo.isTrialTier === true;
  const isAiQuotaPaid = aiQuotaReady && aiQuotaInfo.isTrialTier === false;

  return (
    <AdminLayout>
      <div>
        <button
          onClick={() => navigate('/websites')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Websites
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Website Details</h1>

        {/* Website Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="mb-6">
            {editingName ? (
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  autoFocus
                  className="text-2xl font-bold text-gray-900 border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full max-w-lg"
                  disabled={savingName}
                />
                <button
                  onClick={handleSaveName}
                  disabled={savingName}
                  className="inline-flex items-center p-2 text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
                  title="Save"
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setEditingName(false)}
                  disabled={savingName}
                  className="inline-flex items-center p-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                  title="Cancel"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-2 group">
                <h2 className="text-2xl font-bold text-gray-900">{website.name}</h2>
                <button
                  onClick={() => { setNameValue(website.name); setEditingName(true); }}
                  className="inline-flex items-center p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Rename website"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
              </div>
            )}
            <p className="text-gray-600">{website.industry}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                  website.status === 'completed' ? 'bg-green-100 text-green-800' :
                  website.status === 'generating' ? 'bg-blue-100 text-blue-800' :
                  website.status === 'failed' ? 'bg-red-100 text-red-800' :
                  website.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {website.status}
                </span>
                {/* Maintenance mode indicator */}
                {website.maintenance_mode && (
                  <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.2-3a2 2 0 010-3.46l5.2-3a2 2 0 012.16 0l5.2 3a2 2 0 010 3.46l-5.2 3a2 2 0 01-2.16 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v9" />
                    </svg>
                    Maintenance Mode
                  </span>
                )}
                {/* AI Designs V2 indicator */}
                {website.requirements?.extractedInfo?.useAIDesignsV2 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    AI Designs V2
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Deployment Status</label>
              <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                website.deployment_status === 'deployed' ? 'bg-green-100 text-green-800' :
                website.deployment_status === 'deploying' ? 'bg-blue-100 text-blue-800' :
                website.deployment_status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {website.deployment_status || 'pending'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
              <p className="text-gray-900">{website.current_version || 1}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
              <p className="text-gray-900">{new Date(website.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated</label>
              <p className="text-gray-900">{new Date(website.updated_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>

            {website.generation_time_seconds && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Generation Time</label>
                <p className="text-gray-900">
                  {Math.floor(website.generation_time_seconds / 60)}m {website.generation_time_seconds % 60}s
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Actions</h4>
            <div className="flex flex-wrap gap-3">
              {/* View Analytics Button - links to analytics page */}
              <Link
                to="/analytics"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <ChartBarIcon className="h-4 w-4 mr-2" />
                View Analytics
              </Link>

              {/* Edit Code Button - links to code editor */}
              {website.status === 'completed' && website.content && (
                <Link
                  to={`/websites/${websiteId}/code`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <CodeBracketIcon className="h-4 w-4 mr-2" />
                  Edit Code
                </Link>
              )}

              {website.status === 'generating' && (
                <button
                  onClick={handleMarkAsFailed}
                  disabled={actionLoading === 'mark-failed'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'mark-failed' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                      Mark as Failed
                    </>
                  )}
                </button>
              )}

              {website.status === 'failed' && (
                <button
                  onClick={handleRetryGeneration}
                  disabled={actionLoading === 'retry'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'retry' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Retrying...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Retry Generation
                    </>
                  )}
                </button>
              )}

              {/* Regenerate - available for completed/failed/suspended websites with requirements */}
              {(website.status === 'completed' || website.status === 'failed' || website.status === 'suspended') && website.requirements && (
                <button
                  onClick={handleRegenerateWebsite}
                  disabled={actionLoading === 'regenerate'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'regenerate' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Regenerate from Requirements
                    </>
                  )}
                </button>
              )}

              {/* Republish Domain - available for deployed websites with custom domain */}
              {website.custom_domain && website.render_service_id && (
                <button
                  onClick={handleRepublishDomain}
                  disabled={actionLoading === 'republish-domain'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'republish-domain' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Republishing...
                    </>
                  ) : (
                    <>
                      <GlobeAltIcon className="h-4 w-4 mr-2" />
                      Republish Domain
                    </>
                  )}
                </button>
              )}

              {/* Refresh E-commerce Scripts - available for e-commerce/catalog sites */}
              {website.site_type === 'ecommerce' && website.status === 'completed' && (
                <button
                  onClick={handleRefreshEcommerceScripts}
                  disabled={actionLoading === 'refresh-ecommerce'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'refresh-ecommerce' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Refresh E-commerce Scripts
                    </>
                  )}
                </button>
              )}

              {/* Downgrade to Marketing - available for e-commerce/catalog sites */}
              {website.site_type === 'ecommerce' && website.status === 'completed' && (
                <button
                  onClick={handleDowngradeToMarketing}
                  disabled={actionLoading === 'downgrade-marketing'}
                  className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md shadow-sm text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'downgrade-marketing' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downgrading...
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                      Downgrade to Marketing
                    </>
                  )}
                </button>
              )}

              {/* Migrate to Account - available for completed websites when Production DB is configured */}
              {website.status === 'completed' && migrationAvailable && (
                <button
                  onClick={handleOpenMigrateModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                  Migrate Environment
                </button>
              )}

              {/* Maintenance Mode Toggle */}
              <button
                onClick={handleToggleMaintenance}
                disabled={actionLoading === 'toggle-maintenance'}
                className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  website.maintenance_mode
                    ? 'border-yellow-300 text-yellow-800 bg-yellow-50 hover:bg-yellow-100 focus:ring-yellow-500'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-yellow-500'
                }`}
              >
                {actionLoading === 'toggle-maintenance' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.2-3a2 2 0 010-3.46l5.2-3a2 2 0 012.16 0l5.2 3a2 2 0 010 3.46l-5.2 3a2 2 0 01-2.16 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v9" />
                    </svg>
                    {website.maintenance_mode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
                  </>
                )}
              </button>

              {/* Transfer Ownership - available for all websites */}
              <button
                onClick={handleOpenTransferModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowsRightLeftIcon className="h-4 w-4 mr-2" />
                Transfer Ownership
              </button>

              {/* QA CRM / Omni share links — opens the workspace without a real login */}
              <button
                onClick={() => handleMintCrmShareLink('crm')}
                disabled={actionLoading === 'crm-share-crm'}
                className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md shadow-sm text-amber-900 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mint a signed link that opens /crm without Google login (QA only)"
              >
                {actionLoading === 'crm-share-crm' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Minting…
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    CRM share link
                  </>
                )}
              </button>
              <button
                onClick={() => handleMintCrmShareLink('omni')}
                disabled={actionLoading === 'crm-share-omni'}
                className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md shadow-sm text-amber-900 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mint a signed link that opens /omni without Google login (QA only)"
              >
                {actionLoading === 'crm-share-omni' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Minting…
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Omni share link
                  </>
                )}
              </button>

              {/* Unpublish Website - available for deployed websites */}
              {website.deployment_status === 'deployed' && (
                <button
                  onClick={handleUnpublishWebsite}
                  disabled={actionLoading === 'unpublish'}
                  className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md shadow-sm text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'unpublish' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Unpublishing...
                    </>
                  ) : (
                    <>
                      <EyeSlashIcon className="h-4 w-4 mr-2" />
                      Unpublish Website
                    </>
                  )}
                </button>
              )}

              {/* Delete Website - destructive action, always available */}
              <button
                onClick={handleDeleteWebsite}
                disabled={actionLoading === 'delete'}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'delete' ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Website
                  </>
                )}
              </button>
            </div>

            {crmShareLink?.url && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-amber-900">
                      {crmShareLink.path === 'omni' ? 'Omni' : 'CRM'} share link
                      {crmShareLink.expiresAt
                        ? ` · expires ${new Date(crmShareLink.expiresAt).toLocaleString()}`
                        : ''}
                    </p>
                    <p className="mt-1 break-all font-mono text-xs text-amber-800">{crmShareLink.url}</p>
                    <p className="mt-2 text-xs text-amber-700">
                      Opens without Google login. Treat like a password — anyone with the URL gets full CRM/Omni access for this site.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyCrmShareLink}
                    className="inline-flex shrink-0 items-center rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    <ClipboardDocumentIcon className="mr-1.5 h-4 w-4" />
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Owner Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Owner</h3>
          <div className="flex items-center">
            {website.user_picture ? (
              <img src={website.user_picture} alt={website.user_name} className="h-12 w-12 rounded-full mr-4" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                <span className="text-xl text-gray-500">{website.user_name?.[0]?.toUpperCase()}</span>
              </div>
            )}
            <div>
              {website.account_id ? (
                <Link to={`/accounts/${website.account_id}`} className="font-semibold text-blue-600 hover:text-blue-900">
                  {website.user_name}
                </Link>
              ) : (
                <p className="font-semibold text-gray-900">{website.user_name}</p>
              )}
              <p className="text-sm text-gray-600">{website.user_email}</p>
            </div>
          </div>

          {website.session_id && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">Wizard Session</label>
              <Link to={`/wizard-sessions/${website.session_id}`} className="text-blue-600 hover:text-blue-900 text-sm break-all">
                {website.session_id}
              </Link>
            </div>
          )}
        </div>

        <UtmAttributionCard utm={utmAttribution} website={website} className="mb-6" />

        {/* Links */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Links</h3>
          <div className="space-y-3">
            {(website.render_url || website.custom_domain_url) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Live Site</label>
                <a
                  href={website.custom_domain_url || website.render_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-900 break-all"
                >
                  {website.custom_domain_url || website.render_url}
                </a>
              </div>
            )}

            {website.github_url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GitHub Repository</label>
                <a
                  href={website.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-900 break-all"
                >
                  {website.github_url}
                </a>
              </div>
            )}

            {(website.render_service_id || website.render_url) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Render Dashboard</label>
                <a
                  href={website.render_service_id 
                    ? `https://dashboard.render.com/static/${website.render_service_id}`
                    : website.render_url.replace('https://', 'https://dashboard.render.com/web/')
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-900 break-all"
                >
                  View on Render
                </a>
              </div>
            )}

            {website.custom_domain && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain</label>
                <p className="text-gray-900">{website.custom_domain}</p>
              </div>
            )}
          </div>
        </div>

        {/* Site Limits */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Site Limits</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Limit</label>
              <p className="text-xs text-gray-500 mb-2">
                Maximum effective pages (pages × languages). Leave empty to use the global default.
              </p>
              {editingPageLimit ? (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    placeholder="Global default"
                    value={pageLimitValue}
                    onChange={(e) => setPageLimitValue(e.target.value)}
                    className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={handleSavePageLimit}
                    disabled={savingPageLimit}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingPageLimit ? 'Saving...' : 'Save'}
                  </button>
                  {website.page_limit != null && (
                    <button
                      onClick={handleResetPageLimit}
                      disabled={savingPageLimit}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Reset to Default
                    </button>
                  )}
                  <button
                    onClick={() => setEditingPageLimit(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-gray-900 font-medium">
                    {website.page_limit != null ? website.page_limit : 'Global default'}
                  </span>
                  {website.page_limit != null && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      Custom
                    </span>
                  )}
                  <button
                    onClick={handleEditPageLimit}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            {website.site_type === 'ecommerce' && (
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Limit</label>
                <p className="text-xs text-gray-500 mb-2">
                  Maximum number of products this store can have. Leave empty to use the global default.
                </p>

                {ecommerceStats && (
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <div className="text-sm">
                      <span className="text-gray-500">Current products:</span>{' '}
                      <span className="font-semibold text-gray-900">{ecommerceStats.products}</span>
                    </div>
                  </div>
                )}

                {editingProductLimit ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="number"
                      min="1"
                      placeholder="Global default"
                      value={productLimitValue}
                      onChange={(e) => setProductLimitValue(e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={handleSaveProductLimit}
                      disabled={savingProductLimit}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingProductLimit ? 'Saving...' : 'Save'}
                    </button>
                    {website.product_limit != null && (
                      <button
                        onClick={handleResetProductLimit}
                        disabled={savingProductLimit}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Reset to Default
                      </button>
                    )}
                    <button
                      onClick={() => setEditingProductLimit(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-gray-900 font-medium">
                      {website.product_limit != null ? website.product_limit : 'Global default'}
                    </span>
                    {website.product_limit != null && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={handleEditProductLimit}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* AI Customization Quota — trial lifetime cap or paid monthly + top-ups */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Customization Quota
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {aiQuotaLoadFailed
                  ? 'Could not load quota details.'
                  : aiQuotaLoading || !aiQuotaReady
                    ? 'Loading quota details…'
                    : isAiQuotaTrial
                      ? 'Trial accounts: lifetime cap on Change-mode AI assistant turns for this site.'
                      : isAiQuotaPaid
                        ? 'Paid accounts: monthly allowance (resets on the 1st UTC) plus stackable top-up credits. Monthly allowance is consumed first.'
                        : 'Loading quota details…'}
                {aiQuotaReady && aiQuotaInfo.accountTier && (
                  <span className="ml-1 font-medium text-gray-600">
                    Account tier: {aiQuotaInfo.accountTier}
                  </span>
                )}
              </p>

              {aiQuotaLoading && (
                <p className="text-sm text-gray-500 mb-3">Loading…</p>
              )}

              {aiQuotaLoadFailed && !aiQuotaLoading && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <p className="text-sm text-red-600">Failed to load AI quota for this site.</p>
                  <button
                    type="button"
                    onClick={fetchAiQuota}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {aiQuotaReady && aiQuotaInfo.quota && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {isAiQuotaTrial ? (
                    <>
                      <div className="text-sm">
                        <span className="text-gray-500">Used:</span>{' '}
                        <span className="font-semibold text-gray-900">
                          {aiQuotaInfo.quota.used ?? 0} / {aiQuotaInfo.quota.limit ?? '—'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Remaining:</span>{' '}
                        <span className={`font-semibold ${aiQuotaInfo.quota.reached ? 'text-red-600' : 'text-gray-900'}`}>
                          {aiQuotaInfo.quota.remaining ?? 0}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm">
                        <span className="text-gray-500">Monthly:</span>{' '}
                        <span className="font-semibold text-gray-900">
                          {aiQuotaInfo.quota.monthlyUsed ?? 0} / {aiQuotaInfo.quota.monthlyLimit ?? '—'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Top-up remaining:</span>{' '}
                        <span className="font-semibold text-gray-900">
                          {aiQuotaInfo.quota.topUpRemaining ?? 0}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Total remaining:</span>{' '}
                        <span className={`font-semibold ${aiQuotaInfo.quota.reached ? 'text-red-600' : 'text-green-700'}`}>
                          {aiQuotaInfo.quota.totalRemaining ?? 0}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {isAiQuotaPaid && (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-medium text-gray-700 mb-2">Grant complimentary top-up (no payment)</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="10000"
                      value={aiTopupCredits}
                      onChange={(e) => setAiTopupCredits(e.target.value)}
                      className="w-28 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Credits"
                    />
                    <button
                      onClick={handleGrantAiTopup}
                      disabled={grantingAiTopup}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {grantingAiTopup ? 'Granting...' : 'Grant credits'}
                    </button>
                    <button
                      onClick={handleResetMonthlyAiUsage}
                      disabled={savingAiQuota}
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50"
                    >
                      Reset monthly usage to 0
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Top-ups are valid for 1 year and consumed after the monthly allowance is exhausted.
                  </p>
                </div>
              )}

              {isAiQuotaTrial && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <button
                    onClick={handleResetAiQuotaUsage}
                    disabled={savingAiQuota}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50"
                  >
                    Reset trial usage to 0
                  </button>
                </div>
              )}

              {isAiQuotaTrial ? (
                editingAiQuotaLimit ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="number"
                      min="1"
                      placeholder="Trial default"
                      value={aiQuotaLimitValue}
                      onChange={(e) => setAiQuotaLimitValue(e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={handleSaveAiQuotaLimit}
                      disabled={savingAiQuota}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingAiQuota ? 'Saving...' : 'Save'}
                    </button>
                    {website.ai_customization_limit != null && (
                      <button
                        onClick={handleResetAiQuotaLimit}
                        disabled={savingAiQuota}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Reset to Default
                      </button>
                    )}
                    <button
                      onClick={() => setEditingAiQuotaLimit(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-gray-500">Trial limit:</span>{' '}
                      <span className="font-semibold text-gray-900">
                        {website.ai_customization_limit != null
                          ? website.ai_customization_limit
                          : 'Trial default'}
                      </span>
                    </div>
                    {website.ai_customization_limit != null && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={handleEditAiQuotaLimit}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Edit trial limit
                    </button>
                  </div>
                )
              ) : isAiQuotaPaid ? (
                editingMonthlyQuotaLimit ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="number"
                      min="1"
                      placeholder="Tier default (100)"
                      value={monthlyQuotaLimitValue}
                      onChange={(e) => setMonthlyQuotaLimitValue(e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={handleSaveMonthlyQuotaLimit}
                      disabled={savingAiQuota}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingAiQuota ? 'Saving...' : 'Save'}
                    </button>
                    {website.ai_monthly_quota_limit != null && (
                      <button
                        onClick={handleResetMonthlyQuotaLimit}
                        disabled={savingAiQuota}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Reset to Default
                      </button>
                    )}
                    <button
                      onClick={() => setEditingMonthlyQuotaLimit(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-gray-500">Monthly limit:</span>{' '}
                      <span className="font-semibold text-gray-900">
                        {website.ai_monthly_quota_limit != null
                          ? website.ai_monthly_quota_limit
                          : 'Tier default'}
                      </span>
                    </div>
                    {website.ai_monthly_quota_limit != null && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={handleEditMonthlyQuotaLimit}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Edit monthly limit
                    </button>
                  </div>
                )
              ) : null}

              {isAiQuotaPaid && Array.isArray(aiQuotaInfo?.topups) && aiQuotaInfo.topups.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Top-up history</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {aiQuotaInfo.topups.slice(0, 10).map((topup) => (
                      <div key={topup.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600 border border-gray-100 rounded px-2 py-1.5">
                        <span className="font-medium text-gray-800">
                          {topup.remainingCredits}/{topup.totalCredits} left
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>{topup.paymentProvider === 'admin' ? 'Admin grant' : topup.paymentProvider}</span>
                        <span className="text-gray-400">·</span>
                        <span>{topup.purchasedAt ? new Date(topup.purchasedAt).toLocaleDateString() : '—'}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          topup.displayStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {topup.displayStatus}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp template message credits */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Template Credits
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Prepaid credits consumed by owner-sent WhatsApp templates, test sends, and campaigns.
              </p>

              {whatsappQuotaLoading && (
                <p className="text-sm text-gray-500 mb-3">Loading…</p>
              )}

              {whatsappQuotaLoadFailed && !whatsappQuotaLoading && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <p className="text-sm text-red-600">Failed to load WhatsApp credits for this site.</p>
                  <button
                    type="button"
                    onClick={fetchWhatsappQuota}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {whatsappQuotaInfo?.quota && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Remaining:</span>{' '}
                    <span className={`font-semibold ${whatsappQuotaInfo.quota.remaining <= 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {whatsappQuotaInfo.quota.remaining ?? 0}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Used:</span>{' '}
                    <span className="font-semibold text-gray-900">{whatsappQuotaInfo.quota.used ?? 0}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Reserved:</span>{' '}
                    <span className="font-semibold text-gray-900">{whatsappQuotaInfo.quota.reserved ?? 0}</span>
                  </div>
                </div>
              )}

              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Grant complimentary WhatsApp credits (no payment)</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    value={whatsappTopupCredits}
                    onChange={(e) => setWhatsappTopupCredits(e.target.value)}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Credits"
                  />
                  <button
                    onClick={handleGrantWhatsappTopup}
                    disabled={grantingWhatsappTopup}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {grantingWhatsappTopup ? 'Granting...' : 'Grant credits'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  WhatsApp credits are valid for 1 year and consumed only after Twilio accepts a template send.
                </p>
              </div>

              {Array.isArray(whatsappQuotaInfo?.topups) && whatsappQuotaInfo.topups.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">WhatsApp top-up history</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {whatsappQuotaInfo.topups.slice(0, 10).map((topup) => (
                      <div key={topup.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600 border border-gray-100 rounded px-2 py-1.5">
                        <span className="font-medium text-gray-800">
                          {topup.remainingCredits}/{topup.totalCredits} left
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>{topup.paymentProvider === 'admin' ? 'Admin grant' : topup.paymentProvider}</span>
                        <span className="text-gray-400">·</span>
                        <span>{topup.purchasedAt ? new Date(topup.purchasedAt).toLocaleDateString() : '—'}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          topup.displayStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {topup.displayStatus}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Call minutes
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Prepaid minutes for outbound calls from the Omni back office. A new site starts at zero. Nothing is included with the phone number.
              </p>

              {voiceMinutesLoading && (
                <p className="text-sm text-gray-500 mb-3">Loading…</p>
              )}

              {voiceMinutesLoadFailed && !voiceMinutesLoading && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <p className="text-sm text-red-600">Failed to load call minutes for this site.</p>
                  <button
                    type="button"
                    onClick={fetchVoiceMinutes}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {voiceMinutesInfo?.quota && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Remaining:</span>{' '}
                    <span className={`font-semibold ${voiceMinutesInfo.quota.remaining <= 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {voiceMinutesInfo.quota.remaining ?? 0}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Used:</span>{' '}
                    <span className="font-semibold text-gray-900">{voiceMinutesInfo.quota.used ?? 0}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Reserved:</span>{' '}
                    <span className="font-semibold text-gray-900">{voiceMinutesInfo.quota.reserved ?? 0}</span>
                  </div>
                </div>
              )}

              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Grant minutes now (no payment)</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    value={voiceGrantMinutes}
                    onChange={(e) => setVoiceGrantMinutes(e.target.value)}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Minutes"
                  />
                  <button
                    type="button"
                    onClick={handleGrantVoiceMinutes}
                    disabled={grantingVoiceMinutes}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {grantingVoiceMinutes ? 'Granting...' : 'Grant minutes'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  These minutes last 12 months, the same as a purchase.
                </p>
              </div>

              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Automatic monthly top-up</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={voiceMonthlyMinutes}
                    onChange={(e) => setVoiceMonthlyMinutes(e.target.value)}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Minutes"
                  />
                  <button
                    type="button"
                    onClick={handleSaveVoiceMonthly}
                    disabled={savingVoiceMonthly}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingVoiceMonthly ? 'Saving...' : 'Save monthly amount'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Adds this many minutes on the 1st of each month. 0 turns it off. Monthly minutes expire at the end of that month.
                  {voiceMinutesInfo?.quota?.lastGrantPeriod
                    ? ` Last granted: ${voiceMinutesInfo.quota.lastGrantPeriod}.`
                    : ' Not granted yet.'}
                </p>
              </div>

              {Array.isArray(voiceMinutesInfo?.topups) && voiceMinutesInfo.topups.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Minute history</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {voiceMinutesInfo.topups.slice(0, 10).map((topup) => (
                      <div key={topup.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600 border border-gray-100 rounded px-2 py-1.5">
                        <span className="font-medium text-gray-800">
                          {topup.remainingMinutes}/{topup.totalMinutes} min left
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>
                          {topup.source === 'monthly'
                            ? 'Monthly allowance'
                            : topup.paymentProvider === 'admin'
                              ? 'Admin grant'
                              : 'Purchase'}
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>{topup.purchasedAt ? new Date(topup.purchasedAt).toLocaleDateString() : '—'}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          topup.displayStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {topup.displayStatus}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* WhatsApp Lead Bot — monthly conversation cap (distinct customers
                per calendar month). Only shown when the site has a bot. New
                customers beyond the cap are paused (leads still captured);
                already-served customers keep getting replies. */}
            {waBotLimit && (
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Lead Bot — Monthly Conversations
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Distinct customers the bot will auto-reply to per calendar month. When the cap is
                  reached, new customers are paused (their lead is still captured) until the next
                  month. Leave empty to use the platform default.
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Used this month:</span>{' '}
                    <span className={`font-semibold ${waBotLimit.reached ? 'text-red-600' : 'text-gray-900'}`}>
                      {waBotLimit.used} / {waBotLimit.limit}
                    </span>
                  </div>
                  {waBotLimit.reached && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Limit reached
                    </span>
                  )}
                </div>

                {editingWaBotLimit ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="number"
                      min="0"
                      placeholder="Platform default"
                      value={waBotLimitValue}
                      onChange={(e) => setWaBotLimitValue(e.target.value)}
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={handleSaveWaBotLimit}
                      disabled={savingWaBotLimit}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingWaBotLimit ? 'Saving...' : 'Save'}
                    </button>
                    {waBotLimit.override != null && (
                      <button
                        onClick={handleResetWaBotLimit}
                        disabled={savingWaBotLimit}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Reset to Default
                      </button>
                    )}
                    <button
                      onClick={() => setEditingWaBotLimit(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-gray-500">Limit:</span>{' '}
                      <span className="font-semibold text-gray-900">
                        {waBotLimit.override != null ? waBotLimit.override : 'Platform default'}
                      </span>
                    </div>
                    {waBotLimit.override != null && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={handleEditWaBotLimit}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Edit limit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* E-commerce Data */}
        {website.site_type === 'ecommerce' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              E-commerce Data
              {website.ecommerce_mode === 'catalog' && (
                <span className="ml-2 text-sm font-normal text-gray-500">(Catalog Mode)</span>
              )}
              {website.ecommerce_mode === 'courses' && (
                <span className="ml-2 text-sm font-normal text-indigo-700">(Courses Mode)</span>
              )}
            </h3>
            {ecommerceStatsLoading ? (
              <div className="text-sm text-gray-500">Loading e-commerce data...</div>
            ) : ecommerceStats ? (
              <div className="space-y-6">
                {/* Summary Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{ecommerceStats.products}</div>
                    <div className="text-sm text-gray-500">Products</div>
                    {ecommerceStats.activeProducts !== ecommerceStats.products && (
                      <div className="text-xs text-green-600 mt-1">{ecommerceStats.activeProducts} active</div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{ecommerceStats.variants}</div>
                    <div className="text-sm text-gray-500">Variants</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{ecommerceStats.categories}</div>
                    <div className="text-sm text-gray-500">Categories</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{ecommerceStats.orders}</div>
                    <div className="text-sm text-gray-500">Orders</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{ecommerceStats.customers}</div>
                    <div className="text-sm text-gray-500">Customers</div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Payment Methods ({ecommerceStats.paymentMethodCount})
                  </h4>
                  {ecommerceStats.paymentMethods && ecommerceStats.paymentMethods.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {ecommerceStats.paymentMethods.map((pm, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            pm.connection_status === 'connected'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {pm.provider}
                          <span className="ml-1.5 text-xs opacity-75">
                            ({pm.connection_status || 'unknown'})
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No payment methods configured</span>
                  )}
                </div>

                {/* Shipping Methods */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Shipping Methods ({ecommerceStats.shippingMethodCount})
                  </h4>
                  {ecommerceStats.shippingMethods && ecommerceStats.shippingMethods.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {ecommerceStats.shippingMethods.map((sm, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {sm.name}
                          {sm.is_pickup && <span className="ml-1 text-xs opacity-75">(Pickup)</span>}
                          {sm.price != null && (
                            <span className="ml-1.5 text-xs opacity-75">
                              ${parseFloat(sm.price).toFixed(2)}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No shipping methods configured</span>
                  )}
                </div>

                {/* Coupons */}
                {ecommerceStats.coupons > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">
                      Coupons: {ecommerceStats.coupons}
                    </h4>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400">No e-commerce data available</div>
            )}
          </div>
        )}

        {/* Page SEO Meta Data */}
        {website.content && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                <h3 className="text-xl font-bold text-gray-900">Page SEO Meta Data</h3>
              </div>
              {pagesMetaLoading && (
                <span className="text-sm text-gray-500">Loading...</span>
              )}
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              View and edit SEO meta data for each page. The <code className="bg-gray-100 px-1 rounded">meta.title</code> is used during publish 
              and will override the HTML title if different. For multi-language sites, "Sync to HTML" will also update all translation files.
            </p>

            {pagesMeta.length > 0 ? (
              <div className="space-y-4">
                {pagesMeta.map((page) => (
                  <div 
                    key={page.pageIndex} 
                    className={`border rounded-lg p-4 ${page.hasMismatch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{page.name}</span>
                          <span className="text-sm text-gray-500">({page.path})</span>
                          {page.hasMismatch && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-200 text-yellow-800">
                              <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                              Title Mismatch
                            </span>
                          )}
                        </div>
                      </div>
                      {editingPageMeta !== page.pageIndex && (
                        <button
                          onClick={() => handleEditPageMeta(page)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>

                    {editingPageMeta === page.pageIndex ? (
                      // Edit Mode
                      <div className="space-y-4">
                        {/* Multi-language editing */}
                        {page.isMultiLanguage && siteLanguages.length > 0 ? (
                          <div className="space-y-4">
                            <div className="text-sm text-gray-500 mb-2">
                              Edit title and description for each language:
                            </div>
                            {siteLanguages.map(lang => (
                              <div key={lang} className={`p-3 rounded-lg border ${lang === defaultLanguage ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-semibold text-gray-700 uppercase">{lang}</span>
                                  {lang === defaultLanguage && (
                                    <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">Default</span>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                                    <input
                                      type="text"
                                      value={languageFormData[lang]?.title || ''}
                                      onChange={(e) => handleLanguageFormChange(lang, 'title', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      placeholder={`Title in ${lang.toUpperCase()}`}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                    <textarea
                                      value={languageFormData[lang]?.description || ''}
                                      onChange={(e) => handleLanguageFormChange(lang, 'description', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      rows={2}
                                      placeholder={`Description in ${lang.toUpperCase()}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Single-language editing
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                              <input
                                type="text"
                                value={metaFormData.title}
                                onChange={(e) => setMetaFormData({ ...metaFormData, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Page title for SEO"
                              />
                              {page.htmlTitle && page.htmlTitle !== metaFormData.title && (
                                <p className="text-xs text-yellow-600 mt-1">
                                  HTML title is different: "{page.htmlTitle}"
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                              <textarea
                                value={metaFormData.description}
                                onChange={(e) => setMetaFormData({ ...metaFormData, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={2}
                                placeholder="Page description for SEO"
                              />
                            </div>
                          </>
                        )}
                        
                        {/* Keywords - shared across languages */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
                          <input
                            type="text"
                            value={metaFormData.keywords}
                            onChange={(e) => setMetaFormData({ ...metaFormData, keywords: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="keyword1, keyword2, keyword3"
                          />
                        </div>
                        
                        <div className="flex items-center gap-3 pt-2">
                          <button
                            onClick={() => handleSavePageMeta(page.pageIndex, false)}
                            disabled={savingMeta}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingMeta ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => handleSavePageMeta(page.pageIndex, true)}
                            disabled={savingMeta}
                            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                            title="Also update HTML tags (uses default language values)"
                          >
                            {savingMeta ? 'Saving...' : 'Save & Sync to HTML'}
                          </button>
                          <button
                            onClick={handleCancelEditMeta}
                            disabled={savingMeta}
                            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="space-y-2 text-sm">
                        {/* Multi-language view */}
                        {page.isMultiLanguage && page.languageData && Object.keys(page.languageData).length > 0 ? (
                          <div className="space-y-3">
                            {Object.entries(page.languageData).map(([lang, data]) => (
                              <div key={lang} className={`p-2 rounded ${data.isDefault ? 'bg-blue-50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-gray-700 uppercase text-xs">{lang}</span>
                                  {data.isDefault && (
                                    <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">Default</span>
                                  )}
                                </div>
                                <div className="text-gray-900">
                                  <span className="font-medium">Title:</span> {data.title || <span className="text-gray-400 italic">Not set</span>}
                                </div>
                                <div className="text-gray-700 text-xs line-clamp-1">
                                  <span className="font-medium">Desc:</span> {data.description || <span className="text-gray-400 italic">Not set</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Single-language view
                          <>
                            <div className="flex gap-2">
                              <span className="font-medium text-gray-600 w-24">Meta Title:</span>
                              <span className="text-gray-900">{page.meta?.title || <span className="text-gray-400 italic">Not set</span>}</span>
                            </div>
                            {page.hasMismatch && (
                              <div className="flex gap-2 text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                                <span className="font-medium w-24">HTML Title:</span>
                                <span>{page.htmlTitle}</span>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <span className="font-medium text-gray-600 w-24">Description:</span>
                              <span className="text-gray-900 line-clamp-2">{page.meta?.description || <span className="text-gray-400 italic">Not set</span>}</span>
                            </div>
                          </>
                        )}
                        <div className="flex gap-2">
                          <span className="font-medium text-gray-600 w-24">Keywords:</span>
                          <span className="text-gray-900">
                            {page.meta?.keywords?.length > 0 
                              ? page.meta.keywords.join(', ') 
                              : <span className="text-gray-400 italic">Not set</span>
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                {pagesMetaLoading ? 'Loading page meta data...' : 'No pages found'}
              </p>
            )}
          </div>
        )}

        {/* Requirements */}
        {website.requirements && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Requirements</h3>
            <div className="space-y-6">
              {/* Business Information */}
              <RequirementsSection title="Business Information">
                <RequirementItem label="Business Name" value={website.requirements.businessName} />
                <RequirementItem label="Industry" value={website.requirements.industry} />
                <RequirementItem label="Description" value={website.requirements.description} />
                <RequirementItem label="Target Audience" value={website.requirements.targetAudience} />
                <RequirementItem label="Language" value={website.requirements.language} />
              </RequirementsSection>

              {/* Brand Information */}
              {website.requirements.brandInfo && (
                <RequirementsSection title="Brand Information">
                  <RequirementItem label="Design Style" value={website.requirements.brandInfo.style?.name} />
                  <RequirementItem label="Source" value={website.requirements.brandInfo.style?.source} />
                  <RequirementItem label="Personality" value={website.requirements.brandInfo.personality} />
                  {website.requirements.brandInfo.selectedLogo && (
                    <>
                      <RequirementItem label="Logo" value={website.requirements.brandInfo.selectedLogo.name} />
                      <RequirementItem label="Logo URL" value={website.requirements.brandInfo.selectedLogo.url} />
                    </>
                  )}
                </RequirementsSection>
              )}

              {/* Content Information */}
              {website.requirements.contentInfo && (
                <RequirementsSection title="Content Information">
                  <RequirementItem label="Business Services" value={website.requirements.contentInfo.services} />
                  <RequirementItem label="Unique Selling Points" value={website.requirements.contentInfo.uniqueSellingPoints} />
                  <RequirementItem label="Call to Action" value={website.requirements.contentInfo.callToAction} />
                </RequirementsSection>
              )}

              {/* Visual Information */}
              {website.requirements.visualInfo && (
                <RequirementsSection title="Visual Information">
                  <RequirementItem label="Color Preferences" value={website.requirements.visualInfo.colorPreferences} />
                  <RequirementItem label="Image Style" value={website.requirements.visualInfo.imageStyle} />
                </RequirementsSection>
              )}

              {/* SEO Keywords */}
              {website.requirements.extractedInfo?.seoKeywords && website.requirements.extractedInfo.seoKeywords.length > 0 && (
                <RequirementsSection title="SEO Keywords">
                  <div className="flex flex-wrap gap-2">
                    {website.requirements.extractedInfo.seoKeywords.map((keyword, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {typeof keyword === 'string' ? keyword : keyword.keyword || keyword.text || JSON.stringify(keyword)}
                      </span>
                    ))}
                  </div>
                </RequirementsSection>
              )}

              {/* Color Palette - if available from extracted styles */}
              {website.requirements.extractedInfo?.extractedStyles?.colorPalette && (
                <RequirementsSection title="Color Palette">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(website.requirements.extractedInfo.extractedStyles.colorPalette).map(([name, color]) => (
                      <div key={name} className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded border border-gray-300" 
                          style={{ backgroundColor: typeof color === 'string' ? color : '#ccc' }}
                        />
                        <div>
                          <p className="text-xs font-medium text-gray-700 capitalize">{name.replace(/([A-Z])/g, ' $1').trim()}</p>
                          <p className="text-xs text-gray-500">{typeof color === 'string' ? color : 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </RequirementsSection>
              )}

              {/* AI Design Layout Specs - for AI-generated designs */}
              {/* Check both locations: selectedDribbbleDesigns[0].layoutSpec OR extractedInfo.designLayoutSpec */}
              {(website.requirements.selectedDribbbleDesigns?.[0]?.layoutSpec || website.requirements.extractedInfo?.designLayoutSpec) && (
                <RequirementsSection title="AI Design Layout Specification">
                  <LayoutSpecDisplay 
                    layoutSpec={
                      website.requirements.selectedDribbbleDesigns?.[0]?.layoutSpec || 
                      website.requirements.extractedInfo?.designLayoutSpec
                    } 
                  />
                </RequirementsSection>
              )}

              {/* Extracted Styles Details */}
              {website.requirements.extractedInfo?.extractedStyles && (
                <RequirementsSection title="Extracted Design Styles">
                  <ExtractedStylesDisplay styles={website.requirements.extractedInfo.extractedStyles} />
                </RequirementsSection>
              )}

              {/* V2 Revamp extraction artifacts: mockup, band manifest, sanity, and extracted images */}
              {v2RevampExtraction && (
                <RequirementsSection title="V2 Revamp Extraction">
                  <V2RevampExtractionDisplay extraction={v2RevampExtraction} />
                </RequirementsSection>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  IMPORT SITE DATA - comprehensive display for full site clone
                  ═══════════════════════════════════════════════════════════ */}
              {website.requirements.fullSiteClone && (() => {
                const esd = website.requirements.extractedSiteData || {};
                const brandDna = website.requirements.importBrandDna;
                const layout = website.requirements.extractedLayout || esd.layoutStructure;
                return (
                  <>
                    {/* Overview */}
                    <RequirementsSection title="Import Site Overview">
                      <RequirementItem label="Mode" value="Full Site Clone" />
                      <RequirementItem label="Source URL" value={
                        esd.url ? <a href={esd.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{esd.url}</a> : 'N/A'
                      } />
                      <RequirementItem label="Business Name" value={esd.businessInfo?.name || website.requirements.businessInfo?.name} />
                      <RequirementItem label="Business Description" value={esd.businessInfo?.description || website.requirements.businessInfo?.industry} />
                      <RequirementItem label="Pages Crawled" value={esd.crawledPages?.length || 0} />
                      <RequirementItem label="Images Extracted" value={esd.allImages?.length || 0} />
                      <RequirementItem label="Per-Page Content" value={
                        esd.perPageContent
                          ? `${Object.keys(esd.perPageContent).length} pages`
                          : esd.hasPerPageContent ? 'Stored separately' : 'N/A'
                      } />
                      {esd.logo && (
                        <RequirementItem label="Logo" value={
                          <a href={esd.logo} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all text-xs">{esd.logo}</a>
                        } />
                      )}
                    </RequirementsSection>

                    {/* Crawled Pages */}
                    {esd.crawledPages && esd.crawledPages.length > 0 && (
                      <RequirementsSection title={`Crawled Pages (${esd.crawledPages.length})`}>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {esd.crawledPages.map((page, idx) => {
                            const pageUrl = typeof page === 'string' ? page : (page.url || page.path || '');
                            const pageName = typeof page === 'object' ? (page.name || page.title || '') : '';
                            return (
                              <div key={idx} className="flex items-center gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                                <span className="text-gray-400 w-5 text-right flex-shrink-0">{idx + 1}.</span>
                                {pageName && <span className="font-medium text-gray-700">{pageName}</span>}
                                <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{pageUrl}</a>
                              </div>
                            );
                          })}
                        </div>
                      </RequirementsSection>
                    )}

                    {/* Extracted Color Palette */}
                    {esd.colorScheme && esd.colorScheme.length > 0 && (
                      <RequirementsSection title="Extracted Color Palette">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
                          {esd.colorScheme.map((c, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                                style={{ backgroundColor: c.hex || c.color || '#ccc' }}
                              />
                              <div className="min-w-0">
                                <span className="text-xs font-medium text-gray-800 block">{c.name || `Color ${idx + 1}`}</span>
                                <span className="text-xs text-gray-500">{c.hex || c.color}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {esd.colorFrequency && esd.colorFrequency.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Color frequency data ({esd.colorFrequency.length} colors detected)</summary>
                            <div className="mt-2 max-h-40 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead><tr className="text-left text-gray-500 border-b">
                                  <th className="pb-1">Color</th><th className="pb-1">Count</th><th className="pb-1">Weight</th><th className="pb-1">Contexts</th>
                                </tr></thead>
                                <tbody>
                                  {esd.colorFrequency.slice(0, 20).map((cf, idx) => (
                                    <tr key={idx} className="border-b border-gray-50">
                                      <td className="py-1 flex items-center gap-1">
                                        <div className="w-3 h-3 rounded border border-gray-300 flex-shrink-0" style={{ backgroundColor: cf.color }} />
                                        <span className="text-gray-600">{cf.color}</span>
                                      </td>
                                      <td className="py-1 text-gray-600">{cf.count}</td>
                                      <td className="py-1 text-gray-600">{cf.weight}</td>
                                      <td className="py-1 text-gray-500">{(cf.contexts || []).join(', ')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        )}
                      </RequirementsSection>
                    )}

                    {/* Extracted Typography */}
                    {esd.typography && Object.keys(esd.typography).length > 0 && (
                      <RequirementsSection title="Extracted Typography">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(esd.typography).map(([element, styles]) => {
                            if (!styles || typeof styles !== 'object') return null;
                            const fontFamily = (styles.fontFamily || '').split(',')[0]?.replace(/['"]/g, '').trim();
                            return (
                              <div key={element} className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">{element}</span>
                                <div className="space-y-1 text-xs">
                                  {fontFamily && <div><span className="text-gray-500">Font:</span> <span className="font-medium text-gray-800">{fontFamily}</span></div>}
                                  {styles.fontSize && <div><span className="text-gray-500">Size:</span> <span className="text-gray-700">{styles.fontSize}</span></div>}
                                  {styles.fontWeight && <div><span className="text-gray-500">Weight:</span> <span className="text-gray-700">{styles.fontWeight}</span></div>}
                                  {styles.lineHeight && <div><span className="text-gray-500">Line Height:</span> <span className="text-gray-700">{styles.lineHeight}</span></div>}
                                  {styles.letterSpacing && styles.letterSpacing !== 'normal' && (
                                    <div><span className="text-gray-500">Letter Spacing:</span> <span className="text-gray-700">{styles.letterSpacing}</span></div>
                                  )}
                                  {styles.color && <div className="flex items-center gap-1">
                                    <span className="text-gray-500">Color:</span>
                                    <div className="w-3 h-3 rounded border border-gray-300 inline-block" style={{ backgroundColor: styles.color }} />
                                    <span className="text-gray-700">{styles.color}</span>
                                  </div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </RequirementsSection>
                    )}

                    {/* Extracted Layout Structure */}
                    {layout && (
                      <RequirementsSection title="Extracted Layout Structure">
                        {layout.navigation && (
                          <div className="mb-4">
                            <h5 className="font-semibold text-gray-700 text-sm mb-2">Navigation</h5>
                            {layout.navigation.items && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {(Array.isArray(layout.navigation.items) ? layout.navigation.items : []).map((item, idx) => (
                                  <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">{typeof item === 'string' ? item : item.name || item.label || JSON.stringify(item)}</span>
                                ))}
                              </div>
                            )}
                            {layout.navigation.style && <p className="text-xs text-gray-500">Style: {layout.navigation.style}</p>}
                            {layout.navigation.cta && <p className="text-xs text-gray-500">CTA: {typeof layout.navigation.cta === 'string' ? layout.navigation.cta : (layout.navigation.cta.text || JSON.stringify(layout.navigation.cta))}</p>}
                          </div>
                        )}
                        {layout.sections && layout.sections.length > 0 && (
                          <div className="mb-4">
                            <h5 className="font-semibold text-gray-700 text-sm mb-2">Sections ({layout.sections.length})</h5>
                            <div className="space-y-2">
                              {layout.sections.map((section, idx) => (
                                <div key={idx} className="border-l-2 border-blue-400 pl-3 py-1">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-xs font-semibold text-blue-700 capitalize">{section.type || 'unknown'}</span>
                                    {section.imageCount > 0 && <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">{section.imageCount} img</span>}
                                  </div>
                                  {section.heading && <p className="text-xs text-gray-700">{typeof section.heading === 'string' ? section.heading : JSON.stringify(section.heading)}</p>}
                                  {section.description && <p className="text-xs text-gray-500">{typeof section.description === 'string' ? section.description : JSON.stringify(section.description)}</p>}
                                  {section.layout && <p className="text-xs text-gray-500 italic">{typeof section.layout === 'string' ? section.layout : JSON.stringify(section.layout)}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {layout.footer && (
                          <div>
                            <h5 className="font-semibold text-gray-700 text-sm mb-1">Footer</h5>
                            <p className="text-xs text-gray-600">{typeof layout.footer === 'string' ? layout.footer : (layout.footer.style || layout.footer.description || JSON.stringify(layout.footer))}</p>
                          </div>
                        )}
                      </RequirementsSection>
                    )}

                    {/* Extracted Images (original URLs) */}
                    {esd.allImages && esd.allImages.length > 0 && (
                      <RequirementsSection title={`Extracted Images (${esd.allImages.length})`}>
                        <div className="max-h-96 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="text-left text-gray-500 border-b sticky top-0 bg-white">
                              <th className="pb-1 w-8">#</th>
                              <th className="pb-1">URL</th>
                              <th className="pb-1 w-20">Score</th>
                              <th className="pb-1">Context</th>
                              <th className="pb-1">Source Page</th>
                            </tr></thead>
                            <tbody>
                              {esd.allImages.map((img, idx) => (
                                <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                                  <td className="py-1 text-gray-400">{idx + 1}</td>
                                  <td className="py-1">
                                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{img.url}</a>
                                    {img.alt && <div className="text-gray-400 truncate max-w-xs" title={img.alt}>{img.alt}</div>}
                                  </td>
                                  <td className="py-1 text-gray-600">{img.score ?? '—'}</td>
                                  <td className="py-1 text-gray-500 truncate max-w-[120px]" title={img.context}>{img.context || '—'}</td>
                                  <td className="py-1 text-gray-500 truncate max-w-[150px]" title={img.sourcePageUrl}>
                                    {img.sourcePageUrl ? (() => {
                                      try { return new URL(img.sourcePageUrl).pathname; } catch { return img.sourcePageUrl; }
                                    })() : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </RequirementsSection>
                    )}

                    {/* Import Layout Spec (persisted from importLayoutSpecBuilder) */}
                    {website.requirements.importLayoutSpec && (
                      <RequirementsSection title="Generated Layout Specification (importLayoutSpecBuilder)">
                        <LayoutSpecDisplay layoutSpec={website.requirements.importLayoutSpec} />
                      </RequirementsSection>
                    )}

                    {/* Import Brand DNA */}
                    {brandDna && (
                      <RequirementsSection title="Computed Brand DNA">
                        {brandDna.colorPalette && (
                          <div className="mb-3">
                            <span className="text-sm font-medium text-gray-600 block mb-2">Color Palette (computed):</span>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {Object.entries(brandDna.colorPalette).map(([name, color]) => (
                                <div key={name} className="flex items-center gap-2">
                                  <div
                                    className="w-5 h-5 rounded border border-gray-300"
                                    style={{ backgroundColor: typeof color === 'string' ? color : '#ccc' }}
                                  />
                                  <span className="text-xs text-gray-700 capitalize">{name}: {typeof color === 'string' ? color : 'N/A'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <RequirementItem label="Heading Font" value={brandDna.typography?.headingFont} />
                        <RequirementItem label="Body Font" value={brandDna.typography?.bodyFont} />
                        <RequirementItem label="Design Mood" value={brandDna.designMood} />
                        <RequirementItem label="Visual Style" value={brandDna.visualStyle} />
                        <RequirementItem label="Hero Visual Style" value={brandDna.heroVisualStyle} />
                      </RequirementsSection>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Applied Integrations */}
        {website.applied_integrations && website.applied_integrations.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Applied Integrations</h3>
            <div className="space-y-2">
              {website.applied_integrations.map((integration, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded">
                  <p className="font-semibold text-gray-900">{integration.name || integration.id}</p>
                  {integration.description && (
                    <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Version History */}
        {versions.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Version History</h3>
              {(() => {
                // Calculate purgeable versions (not 1, 2, or current)
                const currentVer = website.current_version || 1;
                const keepSet = new Set([1, 2, currentVer]);
                const purgeCount = versions.filter(v => !keepSet.has(v.version)).length;
                
                return purgeCount > 0 && (
                  <button
                    onClick={handlePurgeVersions}
                    disabled={purgingVersions}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                      purgingVersions
                        ? 'bg-gray-200 text-gray-500 cursor-wait'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                    title="Delete all versions except 1, 2, and current"
                  >
                    {purgingVersions ? (
                      <>
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Purging...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="h-3.5 w-3.5" />
                        Purge History ({purgeCount})
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
            <div className="space-y-3">
              {versions.map((version) => (
                <div 
                  key={version.id} 
                  className={`p-4 border rounded-lg ${
                    version.version === website.current_version 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-gray-900">
                        Version {version.version}
                      </span>
                      {version.version === website.current_version && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-500 text-white">
                          Current
                        </span>
                      )}
                      {version.change_type && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
                          {version.change_type.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      {version.content_size_bytes && (
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          version.content_size_bytes > 10 * 1024 * 1024 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {version.content_size_bytes < 1024 
                            ? `${version.content_size_bytes} B`
                            : version.content_size_bytes < 1024 * 1024 
                              ? `${(version.content_size_bytes / 1024).toFixed(1)} KB`
                              : `${(version.content_size_bytes / (1024 * 1024)).toFixed(2)} MB`
                          }
                        </span>
                      )}
                      <span className="text-sm text-gray-600">
                        {new Date(version.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    {version.description ? (
                      <p className="text-sm text-gray-600">{version.description}</p>
                    ) : (
                      <span></span>
                    )}
                    {version.version !== website.current_version && (
                      <button
                        onClick={() => handleSwitchToVersion(version.version)}
                        disabled={switchingToVersion !== null}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          switchingToVersion === version.version
                            ? 'bg-gray-200 text-gray-500 cursor-wait'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        {switchingToVersion === version.version ? (
                          <span className="flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Restoring...
                          </span>
                        ) : (
                          '↩ Restore'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {versions.length === 0 && (
              <p className="text-gray-500 text-center py-4">No version history available</p>
            )}
          </div>
        )}
      </div>

      {/* Migrate to Account Modal */}
      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseTransferModal}
            />
            
            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Transfer Website Ownership
                  </h3>
                  <button
                    onClick={handleCloseTransferModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Current Owner Info */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Current Owner</p>
                  <div className="flex items-center">
                    {website.user_picture ? (
                      <img src={website.user_picture} alt={website.user_name} className="h-8 w-8 rounded-full mr-3" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        <span className="text-sm text-gray-500">{website.user_name?.[0]?.toUpperCase()}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{website.user_name}</p>
                      <p className="text-xs text-gray-500">{website.user_email}</p>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will transfer the website to a different account.
                    The new account owner will have full access to this website. 
                    All content, deployment, and settings will be preserved.
                  </p>
                </div>
                
                {/* Target Account Selection */}
                <div className="mb-4">
                  <AccountSearchSelect
                    selectedAccount={transferTargetAccount}
                    onSelect={setTransferTargetAccount}
                    onClear={() => setTransferTargetAccount(null)}
                    label="New Owner Account"
                    placeholder="Search for target account..."
                    required
                  />
                </div>

                {/* Preview of target account */}
                {transferTargetAccount && (
                  <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      Website will be transferred to <strong>{transferTargetAccount.name}</strong>
                      {transferTargetAccount.primaryEmail && (
                        <> ({transferTargetAccount.primaryEmail})</>
                      )}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
                <button
                  type="button"
                  onClick={handleTransferOwnership}
                  disabled={!transferTargetAccount || isTransferring}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTransferring ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Transferring...
                    </>
                  ) : (
                    'Transfer Ownership'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseTransferModal}
                  disabled={isTransferring}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMigrateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={isMigrating ? undefined : handleCloseMigrateModal}
            />
            
            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Migrate or Duplicate Website
                  </h3>
                  <button
                    onClick={handleCloseMigrateModal}
                    disabled={isMigrating}
                    className="text-gray-400 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Current Environment Indicator */}
                <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Current Server:</strong> {currentEnvironment.toUpperCase()}
                  </p>
                </div>

                {/* Operation Mode Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setMigrateOperation('duplicate')}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        migrateOperation === 'duplicate'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Duplicate</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Copy to target and keep source
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMigrateOperation('move')}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        migrateOperation === 'move'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Move</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Copy to target and delete source
                      </div>
                    </button>
                  </div>
                </div>

                {/* Migration Direction Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Migration Direction
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setMigrateDirection('qa_to_production')}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        migrateDirection === 'qa_to_production'
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">QA → Production</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {currentEnvironment === 'qa' 
                          ? 'Copy from local to Production' 
                          : 'Copy from QA to local'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMigrateDirection('production_to_qa')}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        migrateDirection === 'production_to_qa'
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Production → QA</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {currentEnvironment === 'production' 
                          ? 'Copy from local to QA' 
                          : 'Copy from Production to local'}
                      </div>
                    </button>
                  </div>
                </div>

                {/* Warning */}
                <div className={`mb-4 p-3 rounded-lg ${
                  migrateOperation === 'duplicate'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <p className={`text-sm ${migrateOperation === 'duplicate' ? 'text-green-800' : 'text-yellow-800'}`}>
                    <strong>{migrateOperation === 'duplicate' ? 'Duplicate mode:' : 'Warning:'}</strong> This will {migrateOperation === 'duplicate' ? 'copy' : 'transfer'} the website to the{' '}
                    <strong>{migrateDirection === 'qa_to_production' ? 'Production' : 'QA'}</strong> database and{' '}
                    {migrateOperation === 'duplicate' ? (
                      <>
                        <strong>keep it in the {migrateDirection === 'qa_to_production' ? 'QA' : 'Production'}</strong> database.
                        Live deployment/domain handles are removed from the copy for safety.
                      </>
                    ) : (
                      <>
                        <strong>delete it from the {migrateDirection === 'qa_to_production' ? 'QA' : 'Production'}</strong> database.
                        The GitHub repository and deployment will be preserved.
                      </>
                    )}
                  </p>
                </div>

                {/* Version History Option */}
                <div className="mb-4 p-3 border border-gray-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={migrateIncludeAllVersions}
                      onChange={(e) => setMigrateIncludeAllVersions(e.target.checked)}
                      disabled={isMigrating}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        Include full version history
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">
                        Off copies only the current website version, which is faster and recommended for QA testing.
                        Turn on only if you need the complete version timeline in the target environment.
                      </span>
                    </span>
                  </label>
                </div>
                
                {/* Current Website Info */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    {migrateOperation === 'duplicate' ? 'Duplicating' : 'Migrating'} from <span className="font-medium">{migrateDirection === 'qa_to_production' ? 'QA' : 'Production'}</span>:
                  </p>
                  <p className="font-medium text-gray-900">{website.name}</p>
                  {website.github_url && (
                    <p className="text-xs text-gray-500 mt-1">GitHub: {website.github_url}</p>
                  )}
                </div>
                
                {/* Target Account Selection */}
                <div className="mb-4">
                  <AccountSearchSelect
                    key={migrateDirection} // Reset component when direction changes
                    selectedAccount={migrateTargetAccount}
                    onSelect={setMigrateTargetAccount}
                    onClear={() => setMigrateTargetAccount(null)}
                    label={`Target Account (in ${migrateDirection === 'qa_to_production' ? 'Production' : 'QA'} database)`}
                    placeholder="Search for target account..."
                    required
                    searchEndpoint={(() => {
                      // Determine target environment based on direction
                      const targetEnv = migrateDirection === 'qa_to_production' ? 'production' : 'qa';
                      
                      // If target is same as current environment, use local search
                      if (targetEnv === currentEnvironment) {
                        return '/api/admin/accounts/search';
                      }
                      
                      // Otherwise, use the remote search endpoint
                      return targetEnv === 'production' 
                        ? '/api/admin/accounts/search-production'
                        : '/api/admin/accounts/search-qa';
                    })()}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Note: You're searching accounts in the <strong>{migrateDirection === 'qa_to_production' ? 'Production' : 'QA'}</strong> database
                    {currentEnvironment === (migrateDirection === 'qa_to_production' ? 'production' : 'qa') && ' (local)'}
                  </p>
                </div>
                
                {/* New Name (optional) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website Name (optional)
                  </label>
                  <input
                    type="text"
                    value={migrateNewName}
                    onChange={(e) => setMigrateNewName(e.target.value)}
                    placeholder={website.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to keep the current name
                  </p>
                </div>

                {migrationJob && (
                  <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-teal-900">
                        Background job: {migrationJob.status}
                      </p>
                      <p className="text-xs text-teal-700">
                        {migrationJob.progress?.phase || 'queued'}
                      </p>
                    </div>
                    {migrationJob.progress?.phase === 'copying_versions' && (
                      <div className="mt-2">
                        <div className="h-2 rounded-full bg-teal-100 overflow-hidden">
                          <div
                            className="h-full bg-teal-600 transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.round(((migrationJob.progress.versionsCopied || 0) / Math.max(migrationJob.progress.versionsTotal || 1, 1)) * 100)
                              )}%`
                            }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-teal-700">
                          Copied {migrationJob.progress.versionsCopied || 0} of {migrationJob.progress.versionsTotal || 0} version rows
                        </p>
                      </div>
                    )}
                    {migrationJob.status === 'failed' && migrationJob.errorMessage && (
                      <p className="mt-2 text-xs text-red-700">{migrationJob.errorMessage}</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
                <button
                  type="button"
                  onClick={handleMigrateToAccount}
                  disabled={!migrateTargetAccount || isMigrating}
                  className="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMigrating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {migrateOperation === 'duplicate' ? 'Duplicating...' : 'Migrating...'}
                    </>
                  ) : (
                    migrateOperation === 'duplicate' ? 'Duplicate Website' : 'Migrate Website'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseMigrateModal}
                  disabled={isMigrating}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Helper Components for Requirements Display

const RequirementsSection = ({ title, children }) => (
  <div className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
    <h4 className="text-lg font-semibold text-gray-800 mb-3">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);

const RequirementItem = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  
  const isReactElement = typeof value === 'object' && value !== null && value.$$typeof;
  
  const displayValue = isReactElement
    ? value
    : Array.isArray(value) 
      ? value.map(v => typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)).join(', ') 
      : typeof value === 'object' 
        ? JSON.stringify(value) 
        : String(value);
  
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2">
      <span className="text-sm font-medium text-gray-600 sm:w-48 flex-shrink-0">{label}:</span>
      <span className="text-sm text-gray-900">{displayValue}</span>
    </div>
  );
};

const LayoutSpecDisplay = ({ layoutSpec }) => {
  if (!layoutSpec) return <p className="text-gray-500 text-sm">No layout specification available</p>;

  return (
    <div className="space-y-6 text-sm">
      {/* Overview */}
      {layoutSpec.overview && (
        <div>
          <h5 className="font-semibold text-gray-800 mb-2">Overview</h5>
          <p className="text-gray-700 leading-relaxed">{layoutSpec.overview}</p>
        </div>
      )}

      {/* Color Palette from Layout Spec */}
      {layoutSpec.colorPalette && (
        <div>
          <h5 className="font-semibold text-gray-800 mb-2">Design Color Palette</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(layoutSpec.colorPalette).map(([name, color]) => (
              <div key={name} className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded border border-gray-300" 
                  style={{ backgroundColor: typeof color === 'string' ? color : '#ccc' }}
                />
                <div>
                  <span className="font-medium text-gray-700 capitalize">{name}: </span>
                  <span className="text-gray-600">{typeof color === 'string' ? color : 'N/A'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Typography */}
      {layoutSpec.typography && (
        <div>
          <h5 className="font-semibold text-gray-800 mb-2">Typography</h5>
          <div className="space-y-1">
            {layoutSpec.typography.headingFont && (
              <p><span className="font-medium">Heading Font:</span> {layoutSpec.typography.headingFont}</p>
            )}
            {layoutSpec.typography.bodyFont && (
              <p><span className="font-medium">Body Font:</span> {layoutSpec.typography.bodyFont}</p>
            )}
            {layoutSpec.typography.headingStyle && (
              <p><span className="font-medium">Heading Style:</span> {layoutSpec.typography.headingStyle}</p>
            )}
            {layoutSpec.typography.bodyStyle && (
              <p><span className="font-medium">Body Style:</span> {layoutSpec.typography.bodyStyle}</p>
            )}
          </div>
        </div>
      )}

      {/* Pages */}
      {layoutSpec.pages && layoutSpec.pages.length > 0 && (
        <div>
          <h5 className="font-semibold text-gray-800 mb-3">Page Structure</h5>
          <div className="space-y-4">
            {layoutSpec.pages.map((page, pageIdx) => (
              <div key={pageIdx} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-bold text-gray-900">{page.name}</span>
                  <span className="text-gray-500 text-xs">({page.path})</span>
                </div>
                
                {page.sections && page.sections.length > 0 && (
                  <div className="space-y-3">
                    {page.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="border-l-2 border-blue-400 pl-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-blue-700 capitalize">{section.type}</span>
                          {section.imageCount > 0 && (
                            <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                              {section.imageCount} image{section.imageCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {section.layout && (
                          <p className="text-gray-700 text-xs leading-relaxed mb-1">
                            <span className="font-medium">Layout:</span> {typeof section.layout === 'string' ? section.layout : JSON.stringify(section.layout)}
                          </p>
                        )}
                        {section.content && (
                          <p className="text-gray-600 text-xs leading-relaxed mb-1">
                            <span className="font-medium">Content:</span> {typeof section.content === 'string' ? section.content : JSON.stringify(section.content)}
                          </p>
                        )}
                        {section.visualStyle && (
                          <p className="text-gray-500 text-xs leading-relaxed italic">
                            {typeof section.visualStyle === 'string' ? section.visualStyle : JSON.stringify(section.visualStyle)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const formatV2Value = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const compactV2Object = (value) => {
  if (!value || typeof value !== 'object') return null;
  const entries = Object.entries(value).filter(([, item]) => item !== null && item !== undefined && item !== '');
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

const V2MetaPill = ({ children, color = 'gray' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    gray: 'bg-gray-50 text-gray-700 border-gray-100'
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${colorClasses[color] || colorClasses.gray}`}>
      {children}
    </span>
  );
};

const V2JsonDetails = ({ title, data }) => {
  if (!data) return null;

  return (
    <details className="rounded-lg border border-gray-200 bg-gray-50">
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-gray-800 hover:text-blue-600">
        {title}
      </summary>
      <pre className="max-h-80 overflow-auto border-t border-gray-200 p-3 text-xs text-gray-700 whitespace-pre-wrap">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
};

const V2KeyValue = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="text-xs">
      <span className="font-medium text-gray-600">{label}: </span>
      <span className="text-gray-800 break-words">{formatV2Value(value)}</span>
    </div>
  );
};

const V2RevampExtractionDisplay = ({ extraction }) => {
  if (!extraction) return null;

  const {
    selectedDesign,
    designMetadata,
    sourceMockupUrl,
    layoutSpec,
    extractedStyles,
    bandManifest,
    extractedAssets,
    faithfulExtractionUsed,
    faithfulSanity,
    extractedStylesCompletedAt,
    extractionPending,
    extractionError,
    extractionRetryCount
  } = extraction;

  const styleCategoryCount = extractedStyles ? Object.keys(extractedStyles).length : 0;
  const layoutPageCount = Array.isArray(layoutSpec?.pages) ? layoutSpec.pages.length : 0;
  const sectionCount = Array.isArray(layoutSpec?.pages)
    ? layoutSpec.pages.reduce((total, page) => total + (Array.isArray(page.sections) ? page.sections.length : 0), 0)
    : 0;
  const faithfulStatusColor = faithfulExtractionUsed === true ? 'green' : faithfulExtractionUsed === false ? 'yellow' : 'gray';
  const faithfulStatusLabel = faithfulExtractionUsed === true
    ? 'Faithful extraction used'
    : faithfulExtractionUsed === false
      ? 'Legacy extraction path'
      : 'Extraction path unknown';

  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-500">Extraction Path</p>
          <div className="mt-1">
            <V2MetaPill color={faithfulStatusColor}>{faithfulStatusLabel}</V2MetaPill>
          </div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-500">Layout Spec</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{layoutPageCount} pages</p>
          <p className="text-xs text-gray-500">{sectionCount} sections</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-500">Band Manifest</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{bandManifest.length}</p>
          <p className="text-xs text-gray-500">homepage bands</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-500">Extracted Images</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{extractedAssets.length}</p>
          <p className="text-xs text-gray-500">decorative assets</p>
        </div>
      </div>

      {(sourceMockupUrl || selectedDesign || designMetadata) && (
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h5 className="font-semibold text-gray-800">Selected Source Mockup</h5>
              <p className="text-xs text-gray-500">The design image used as the visual anchor for faithful extraction.</p>
            </div>
            {extractedStylesCompletedAt && (
              <V2MetaPill color="blue">Completed {new Date(extractedStylesCompletedAt).toLocaleString()}</V2MetaPill>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(220px,360px),1fr]">
            {sourceMockupUrl ? (
              <div>
                <a href={sourceMockupUrl} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                  <img
                    src={sourceMockupUrl}
                    alt="Selected V2 source mockup"
                    className="max-h-96 w-full object-contain"
                    loading="lazy"
                  />
                </a>
                <a href={sourceMockupUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block break-all text-xs text-blue-600 hover:underline">
                  {sourceMockupUrl}
                </a>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                No persisted mockup image URL found.
              </div>
            )}

            <div className="space-y-2">
              <V2KeyValue label="Style" value={designMetadata?.styleName || selectedDesign?.styleName || selectedDesign?.title} />
              <V2KeyValue label="Source" value={designMetadata?.source || selectedDesign?.source} />
              <V2KeyValue label="Description" value={designMetadata?.description || selectedDesign?.description} />
              <V2KeyValue label="Extraction Pending" value={extractionPending} />
              <V2KeyValue label="Retry Count" value={extractionRetryCount} />
              {extractionError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-semibold">Extraction error</p>
                  <p>{formatV2Value(extractionError.message || extractionError)}</p>
                  {extractionError.at && <p className="mt-1 text-red-500">{extractionError.at}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {extractedAssets.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-800 mb-1">Extracted Decorative Images</h5>
          <p className="text-xs text-gray-500 mb-3">
            Assets lifted from the source mockup and later injected into generated sections.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {extractedAssets.filter(Boolean).map((asset, idx) => (
              <div key={asset.id || idx} className="rounded-lg border border-gray-200 bg-white p-3">
                {asset.url ? (
                  <a href={asset.url} target="_blank" rel="noopener noreferrer" className="flex h-36 items-center justify-center rounded border border-gray-100 bg-gray-50">
                    <img
                      src={asset.url}
                      alt={asset.alt || `Extracted decorative asset ${idx + 1}`}
                      className="max-h-32 max-w-full object-contain"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="flex h-36 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-500">
                    No asset URL
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1">
                  <V2MetaPill color="purple">{asset.role || 'asset'}</V2MetaPill>
                  <V2MetaPill color={asset.extracted === false ? 'yellow' : 'green'}>
                    {asset.extracted === false ? 'not extracted' : 'extracted'}
                  </V2MetaPill>
                  {asset.sectionIndex !== undefined && (
                    <V2MetaPill color="blue">section {asset.sectionIndex}</V2MetaPill>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  <V2KeyValue label="Alt" value={asset.alt} />
                  <V2KeyValue label="Dominant Color" value={asset.dominantColor} />
                  <V2KeyValue label="Size" value={asset.suggestedSize} />
                  <V2KeyValue label="Priority" value={asset.priority} />
                  <V2KeyValue label="BBox" value={compactV2Object(asset.bbox)} />
                  <V2KeyValue label="Band BBox" value={compactV2Object(asset.bandRelativeBbox)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {bandManifest.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-4">
          <h5 className="font-semibold text-gray-800 mb-1">Band Manifest & Layout Locks</h5>
          <p className="text-xs text-gray-500 mb-3">
            Homepage bands transcribed from the mockup. These drive crops, section prompts, layout locks, and image references.
          </p>
          <div className="space-y-3">
            {bandManifest.map((band, idx) => (
              !band ? (
                /* bandManifest is a SPARSE array: null slots mark homepage
                   sections that are NOT visibly drawn in the source mockup
                   (mockupVisible:false). Render a compact placeholder so the
                   "Band N" numbering still aligns with layoutSpec.pages[0]. */
                <div key={`null-band-${idx}`} className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-800">Band {idx + 1}</span>
                    <V2MetaPill>not in mockup</V2MetaPill>
                  </div>
                </div>
              ) : (
              <details key={band.id || band.index || idx} className="rounded-lg border border-gray-200 bg-gray-50">
                <summary className="cursor-pointer px-3 py-2">
                  <div className="inline-flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-800">Band {idx + 1}</span>
                    <V2MetaPill color="blue">{band.type || band.sectionType || 'section'}</V2MetaPill>
                    {band.compositionPattern && <V2MetaPill color="purple">{band.compositionPattern}</V2MetaPill>}
                    {band.gridColumns && <V2MetaPill>{band.gridColumns} columns</V2MetaPill>}
                    {band.imageAspectRatio && <V2MetaPill>{band.imageAspectRatio}</V2MetaPill>}
                  </div>
                </summary>
                <div className="border-t border-gray-200 p-3 space-y-2">
                  <V2KeyValue label="Purpose" value={band.purpose || band.contentRole || band.description} />
                  <V2KeyValue label="Composition" value={band.composition || band.layout} />
                  <V2KeyValue label="Color Mode" value={band.colorMode} />
                  <V2KeyValue label="Vertical Rhythm" value={band.verticalRhythm} />
                  <V2KeyValue label="Container Width" value={band.containerWidth} />
                  <V2KeyValue label="Hero Content Max Width" value={band.heroContentMaxWidth} />
                  <V2KeyValue label="BBox" value={compactV2Object(band.bbox)} />
                  <V2KeyValue label="Typography" value={compactV2Object(band.typography)} />
                  <V2JsonDetails title="Raw band JSON" data={band} />
                </div>
              </details>
              )
            ))}
          </div>
        </div>
      )}

      {(faithfulSanity || styleCategoryCount > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {faithfulSanity && (
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h5 className="font-semibold text-gray-800">Faithful Sanity Check</h5>
                {faithfulSanity.ok !== undefined && (
                  <V2MetaPill color={faithfulSanity.ok ? 'green' : 'yellow'}>
                    {faithfulSanity.ok ? 'ok' : 'needs review'}
                  </V2MetaPill>
                )}
              </div>
              <V2KeyValue label="Issue" value={faithfulSanity.issue || faithfulSanity.reason} />
              <V2KeyValue label="Missing Bands" value={faithfulSanity.missingBands} />
              <V2KeyValue label="Extra Bands" value={faithfulSanity.extraBands} />
              <V2KeyValue label="Notes" value={faithfulSanity.notes} />
              <div className="mt-3">
                <V2JsonDetails title="Raw sanity JSON" data={faithfulSanity} />
              </div>
            </div>
          )}

          {styleCategoryCount > 0 && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h5 className="font-semibold text-gray-800 mb-2">Extracted Style Payload</h5>
              <p className="text-xs text-gray-500 mb-3">
                Full styles are rendered in the Extracted Design Styles section above.
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(extractedStyles).map(([key, value]) => (
                  <V2MetaPill key={key} color="gray">
                    {key}: {Array.isArray(value) ? value.length : typeof value === 'object' && value ? Object.keys(value).length : 1}
                  </V2MetaPill>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <V2JsonDetails title="Raw selected design JSON" data={selectedDesign} />
        <V2JsonDetails title="Raw design metadata JSON" data={designMetadata} />
      </div>
    </div>
  );
};

const ExtractedStylesDisplay = ({ styles }) => {
  if (!styles) return null;

  const styleCategories = [
    { key: 'typography', label: 'Typography Styles' },
    { key: 'buttonStyles', label: 'Button Styles' },
    { key: 'cardStyles', label: 'Card Styles' },
    { key: 'shadows', label: 'Shadow Styles' },
    { key: 'gradients', label: 'Gradients' },
    { key: 'layout', label: 'Layout Spacing' },
    { key: 'borders', label: 'Border Styles' },
    { key: 'effects', label: 'Effects & Animations' },
    { key: 'imageStyles', label: 'Image Styles' },
    { key: 'sectionBackgrounds', label: 'Section Backgrounds' },
  ];

  // Renders any value as a safe string. Crucially: when an atom carries a
  // `css` object (V2 faithful extraction emits `{ css: { background, color, ... } }`
  // for buttonStyles/cardStyles/imageStyles), serialize its entries instead of
  // returning the raw object — otherwise React throws "Objects are not valid
  // as a React child" because we'd be rendering `{styleObject}` directly.
  const formatStyleValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((v) => formatStyleValue(v)).join(' • ');
    }
    if (typeof value === 'object') {
      if (typeof value.svg === 'string') return '[SVG Icon]';
      if (typeof value.clipPath === 'string') return value.clipPath;
      if (typeof value.value === 'string') return value.value;
      if (value.css !== undefined && value.css !== null) {
        if (typeof value.css === 'string') return value.css;
        if (typeof value.css === 'object') {
          return Object.entries(value.css)
            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join('; ');
        }
      }
      return Object.entries(value)
        .filter(([k]) => k !== 'usedIn' && k !== 'variant' && k !== 'placement' && k !== 'name')
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join('; ');
    }
    return String(value);
  };

  // V2 faithful extraction emits style atoms as ARRAYS of
  // `{ variant?, css?, value?, svg?, clipPath?, placement?, usedIn[] }` objects,
  // while the legacy extractor emits MAPS of `{ primary: {...}, secondary: {...} }`.
  // Render arrays per-atom (showing the variant/role + usedIn pills) and maps
  // per-key (legacy behavior). Both safely route every value through
  // formatStyleValue so we never hand a raw object to React as a child.
  const renderAtomLabel = (atom, idx) => {
    if (!atom || typeof atom !== 'object') return `#${idx + 1}`;
    return atom.variant || atom.name || atom.placement || `#${idx + 1}`;
  };

  return (
    <div className="space-y-4 text-sm">
      {styles.cssVariables && (
        <div>
          <h5 className="font-semibold text-gray-800 mb-2">CSS Variables</h5>
          <div className="bg-gray-100 rounded p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap">
            {typeof styles.cssVariables === 'string'
              ? styles.cssVariables
              : JSON.stringify(styles.cssVariables, null, 2)}
          </div>
        </div>
      )}

      {styleCategories.map(({ key, label }) => {
        const categoryStyles = styles[key];
        if (!categoryStyles || typeof categoryStyles !== 'object') return null;

        const isAtomArray = Array.isArray(categoryStyles);

        return (
          <details key={key} className="group">
            <summary className="font-semibold text-gray-800 cursor-pointer hover:text-blue-600 flex items-center gap-2">
              <span className="group-open:rotate-90 transition-transform">▶</span>
              {label}
              <span className="text-xs text-gray-400 font-normal">
                ({isAtomArray ? categoryStyles.length : Object.keys(categoryStyles).length})
              </span>
            </summary>
            <div className="mt-2 pl-4 space-y-2">
              {isAtomArray
                ? categoryStyles.map((atom, idx) => (
                    <div key={idx} className="text-xs border-l-2 border-gray-200 pl-2">
                      <div className="font-medium text-gray-700 capitalize">
                        {renderAtomLabel(atom, idx)}
                      </div>
                      <div className="text-gray-600 break-all">{formatStyleValue(atom)}</div>
                      {Array.isArray(atom?.usedIn) && atom.usedIn.length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {atom.usedIn.map((role, rIdx) => (
                            <span
                              key={rIdx}
                              className="inline-block bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded"
                            >
                              {String(role)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                : Object.entries(categoryStyles).map(([styleName, styleValue]) => (
                    <div key={styleName} className="text-xs">
                      <span className="font-medium text-gray-700">{styleName}:</span>{' '}
                      <span className="text-gray-600">{formatStyleValue(styleValue)}</span>
                    </div>
                  ))}
            </div>
          </details>
        );
      })}
    </div>
  );
};

export default WebsiteDetail;

